import React, { useState, useEffect, useRef } from "react";
import FeatureErrorBoundary from "../../FeatureErrorBoundary";
import { message, Modal, Form, Drawer, Row, Col, Collapse, Table, Button, DatePicker, Tag, Card, Input, Radio, Checkbox, Spin } from "antd";
import { ExclamationCircleOutlined, ArrowLeftOutlined, ClockCircleOutlined, PrinterOutlined, CheckCircleFilled, CloseCircleFilled, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { clock, timeStamp, timeValidator, currentDay, dateValidator } from "../../../utils/clock";
import { useHistory } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import useDebounce from "../../../lib/hooks/useDebounce";
import DefaultProductImage from "../../../assets/images/default-product.webp";
import db from "../../../database";
import Axios from "axios";
import { getOAuthHeaders } from "../../../constants/oAuthValidation";
import dayjs from "dayjs";
import { SyncData } from "../Restaurant/Tables-Management/SyncData";
import { useEventBus } from "../../../lib/components/EventBusProvider";
import useCartStore from "../../../stores/useCartStore";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import { isPrepareBillModificationBlocked, PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE } from "../../../utils/prepareBillModificationGuard";
import { isCreditSaleModificationBlocked, CREDIT_SALE_MODIFICATION_BLOCKED_MESSAGE } from "../../../utils/creditSaleModificationGuard";
import useAuthStore from "../../../stores/useAuthStore";
import useTransactionStore from "../../../stores/useTransactionStore";
import useReferenceStore from "../../../stores/useReferenceStore";
import useSessionStore from "../../../stores/useSessionStore";
import { trace } from "../../../utils/debugTrace";
import { computeIsExchangeTarget } from "../../../utils/exchangeCartRouting";
import { getCoreRoundOffTender } from "../../../utils/paymentRounding";
import { getTillMode, TILL_MODES } from "../../../constants/accessControl";

// OMS Orders Modal Imports — now in useOMSOperations hook
import CoreModals from "./coreModals";
import ReturnBill from "./returnBill";
import { CheckoutTotalManualDiscount } from "./PricingRules/CheckoutTotalManualDiscount";
import { CheckoutFlatDiscount } from "./PricingRules/CheckoutFlatDiscount";
import { CheckoutPercentageDiscount } from "./PricingRules/CheckoutPercentageDiscount";
import { createCustomer, getCustomer, updateCustomer } from "./customer";
import { pricingRuleController } from "./PricingRules/pricingRuleController";
import { barcodeScaner } from "./scanner";
import { TotalBillDiscount } from "./PricingRules/totalBillDiscount";
import { TotalBillFreeProductDiscount } from "./PricingRules/totalBillFreeProductDiscount";
import { paymentProcess } from "./payment/paymentProcess";
import { addProductToCart } from "./cart";
import { useTranslation } from "react-i18next";
import { addAmount, completePayment } from "./payment/paymentController";
import { stock } from "./stock";
import PoleDisplay from "../../../lib/printer/poleDisplay";
import PrintController from "../../../lib/printer/printController";
import upsertPOSLog from "../Retail/posLog";
import PointOfSuperMarketModals from "../Retail/SuperMarket/SuperMarketModal";
import NotFoundImg from "../../../assets/images/NotFoundImg.svg";
import CWPoleDisplay from "../../../lib/printer/CWPoleDisplay";
import { numberToWords } from "../../../utils/numberToWords";
import styles from "../../../constants/styles.json";
import { getWorkflowData } from "../../../utils/getWorkflowData";
import Close from "../../../assets/images/close-x.svg";
import TextArea from "antd/lib/input/TextArea";
import Keyboard from "react-simple-keyboard";
import CustomerDisplayBridge from "../../CustomerDisplay/CustomerDisplayBridge";
import {
  checkGiftCard,
  confirmPOSLayawayOrder1,
  discardCouponService,
  generateGiftVoucher,
  getNewOmsOrder,
  orderHistoryService,
  posConfigService,
  posOrderProcessor1,
  RFIDTag,
  salesHistoryService,
  updateOmsOrder,
  upsertGiftCardTransaction,
  upsertLoyaltyTransaction,
  upsertPOSLogService,
  verifyCouponsService,
  verifyGiftCardService,
  upsertSupplierPayment,
  getCustomerWallet,
  externalCoupon,
  upsertCart,
  upsertStoreDiscount,
  discardGiftCard,
  getStockForProductService,
  verifyReturn,
  upsertFinFundsTransfer,
  upsertWalletTransactionService,
  getEInvoiceDetails,
  getEInvoiceURLs,
} from "../../../services";
import MenuLayout from "../Restaurant/MenuLayout";
import { WarningOutlined } from "@ant-design/icons";
import messages from "../../../errors/messages.en-IN.json";
import { getApplicablePrice } from "../../../utils/getPriceList";
import { getB2BCustomers, validateGSTNumber, validatePANNo } from "../../../services/index";

// Extracted Hooks
import useCashManagement from "./hooks/useCashManagement";
import useGiftCard from "./hooks/useGiftCard";
import useOrderHistory from "./hooks/useOrderHistory";
import useParkedBills from "./hooks/useParkedBills";
import useCustomerOperations from "./hooks/useCustomerOperations";
import useCartOperations from "./hooks/useCartOperations";
import usePaymentFlow from "./hooks/usePaymentFlow";
import useOrderProcessing from "./hooks/useOrderProcessing";
import useSalesReturn from "./hooks/useSalesReturn";
import useCouponProcessing from "./hooks/useCouponProcessing";
import useProductAddons from "./hooks/useProductAddons";
import useProductOperations from "./hooks/useProductOperations";
import useOMSOperations from "./hooks/useOMSOperations";
import usePOSKeyboard from "./hooks/usePOSKeyboard";
import useZoneNavigation from "./hooks/useZoneNavigation";
import KeyboardShortcutsOverlay from "./modals/KeyboardShortcutsOverlay";
import "./keyboardNavigation.css";
import { roundTo, posLogActivity as posLogActivityUtil, formatDate, parseDateTime, calculateDuration, formatDuration } from "./posUtils";
import { ACTIONS } from "./hooks/keyboardShortcutRegistry";

// Extracted Components
import PrintTemplateModal from "./components/PrintTemplateModal";
import AddonRepeatModal from "./components/AddonRepeatModal";
import NotesDrawer from "./components/NotesDrawer";
import StockWarningModal from "./components/StockWarningModal";
import BOMStockDrawer from "./components/BOMStockDrawer";
import RejectQuotationDrawer from "./components/RejectQuotationDrawer";

// Extracted Hooks (additional)
import useSalesRepresentative from "./hooks/useSalesRepresentative";
import useDiscountOperations from "./hooks/useDiscountOperations";
import useWalletAndBatches from "./hooks/useWalletAndBatches";
import useEBillWebhook from "./hooks/useEBillWebhook";
import useCoreState from "./hooks/useCoreState";
import useCoreEffects from "./hooks/useCoreEffects";
import useCartHandlers from "./hooks/useCartHandlers";
import useKeyboardHandlers from "./hooks/useKeyboardHandlers";

// Utils
import { buildComponentProps } from "./utils/buildComponentProps";
import { hasInvalidTopupPaymentAmount, TOPUP_PAYMENT_AMOUNT_MISSING } from "../../../utils/topupPaymentAmount";

// PointOfsaleCore Component Start
const PointOfsaleCore = (props) => {
  const { t } = useTranslation();
  const RenderComponent = props.component;
  const tillData = useAuthStore.getState().tillData;

  if (!tillData?.tillAccess) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading POS..." />
      </div>
    );
  }

  const currentTillSession = useSessionStore.getState().tillSession;
  const posConfig = usePOSConfigStore.getState().posConfig;

  const shouldCheckTill = getTillMode(posConfig) === TILL_MODES.NORMAL;

  if (shouldCheckTill) {
    if (!currentTillSession || currentTillSession.tillStatus !== "open") {
      window.location.replace("/till");
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <Spin size="large" tip="Redirecting to login..." />
        </div>
      );
    }
  }

  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController);
  const tillLayout = parseInt(tillaccess?.layout ?? 1);
  const precision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2;
  const receiptlessReturn2Enabled = posConfig?.enableReceiptlessReturn2 === "Y";
  const defaultCustomer = tillData.tillAccess.csBunit.b2cCustomer;
  const tillDocumentSequence = useSessionStore.getState().documentSequence || 0;

  // Ensure cart has valid IDs on first POS open (web mode starts with empty cart).
  // Only call setCart when we actually assign a new value — otherwise we create
  // an infinite render loop (setCart → new ref → subscriber re-render → repeat).
  const currentCart = useCartStore.getState().cart;
  if (!currentCart.documentno || !currentCart.sOrderID) {
    const updates = { ...currentCart };
    let changed = false;
    if (!updates.sOrderID) {
      updates.sOrderID = uuidv4().replace(/-/g, "").toUpperCase();
      changed = true;
    }
    if (!updates.documentno && tillData?.tillAccess?.cwrTill?.prefix) {
      updates.documentno = `${tillData.tillAccess.cwrTill.prefix}${tillDocumentSequence + 1}`;
      changed = true;
    }
    if (changed) {
      useCartStore.getState().setCart(updates);
    }
  }

  // Clear stale Return & Exchange state after browser refresh — if cart.isReturn
  // persisted but both return cart and exchange cart are empty, user cannot
  // continue the flow so reset to normal cart mode.
  //
  // This must run exactly once, at mount, against whatever cart state the page
  // loaded with — NOT reactively on every render. It used to run directly in the
  // render body guarded only by a useRef, which doesn't distinguish "stale from a
  // previous page load" from "the user just now legitimately started a blind
  // Return & Exchange in this same session": both look identical the very first
  // time cart.isReturn becomes true (zero return items and an empty exchange cart
  // is exactly what a brand-new session looks like before anything's been added).
  // That made this fire the instant ReturnOptionsModal's Continue handler set
  // isReturn/created the exchange cart, immediately undoing it — cart.isReturn,
  // the exchange cart, and selectedReturnType were all wiped back to null before
  // the split Return/Exchange view could ever render (BUG-723 reopened).
  useEffect(() => {
    const cartAtMount = useCartStore.getState().cart;
    if (cartAtMount.isReturn === true) {
      const returnItems = (cartAtMount.items || []).filter((i) => i.isReturn);
      const exchCart = useCartStore.getState().exchangeCart;
      const exchItems = exchCart?.items || [];
      if (returnItems.length === 0 && exchItems.length === 0) {
        const cleanedCart = { ...cartAtMount };
        delete cleanedCart.isReturn;
        cleanedCart.items = [];
        cleanedCart.tax = 0;
        cleanedCart.total = 0;
        cleanedCart.subtotal = 0;
        cleanedCart.discount = 0;
        cleanedCart.paid = 0;
        cleanedCart.payments = [];
        useCartStore.getState().setCart(cleanedCart);
        useCartStore.getState().setExchangeCart(null);
        useTransactionStore.getState().setSelectedReturnType(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPrintModeXML = tillData.tillAccess.cwrTill.hardwareController.printReceipt === "Y" ? true : false;
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
  const ObFlag = tillData.tillAccess.cwrTill.printTemplate.obController === "Y" ? true : false;
  const CWHardwareController = tillData.tillAccess.cwrTill.printTemplate.cwController === "Y" ? true : false;
  const tillDataPaymentMethods = tillData.tillAccess.csBunit.paymentMethodList;
  const authHeaders = getOAuthHeaders();
  const history = useHistory();
  const tillValue = useAuthStore.getState().tillValue;
  const { dispatchEvent, discountApproval, dispatchItemSelection, dispatchQRCodeEvent, closeQRCodeModal, dispatchCCAvenueEvent, closeCCAvenueModal, dispatchWeight } = useEventBus();
  const pricePrecision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const enableMultiPrintTemplates = tillData?.tillAccess?.cwrTill?.enableMultiPrintTemplates === "Y";
  const printerURL = tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
  const tillId = tillData.tillAccess?.cwrTill?.cwrTillID;
  const userData = useAuthStore.getState().userData;
  const tillSession = useSessionStore.getState().tillSession;
  const returnThisReceipt = useTransactionStore.getState().returnThisReceipt;
  const isExchange = computeIsExchangeTarget({
    selectedReturnType: useTransactionStore.getState().selectedReturnType,
    returnThisReceipt,
    cart: useCartStore.getState().cart,
  });

  // Customer config
  const customerConfig = useReferenceStore.getState().customerConfigs;
  const customerConfigData = customerConfig ? JSON.parse(customerConfig[0]?.configuration) : {};
  const retailCustomerTabs = customerConfigData?.tabs || [];
  const retailCustomerFields = customerConfigData?.fields || [];
  const retailCustomerGroups = customerConfigData?.fieldGroups || [];
  const retailCustomerRules = customerConfigData?.conditionalRules || [];
  const retailCustomerb2bSettings = customerConfigData?.b2bSettings || [];
  const retailCustomerApiDataSources = customerConfigData?.apiDataSources || [];
  const retailCustomerOperationalSettings = customerConfigData?.operationalSettings || [];

  // Print templates
  const printTemplates = tillData?.tillAccess?.cwrTill?.printTemplates ? JSON.parse(tillData?.tillAccess?.cwrTill?.printTemplates) : [];

  // Document types
  const documentTypes = [
    { type: "Invoice", isEnabled: true, isVisible: true },
    {
      type: "Advance",
      isVisible: posConfig?.requireCustomerforLayaway === "Y",
      isEnabled: (usePOSConfigStore.getState().saleTypeData || {})?.cwrSaletype?.enableLayaway === "Y",
    },
    { type: "Quotation", isEnabled: posConfig?.enableQuotation === "Y", isVisible: posConfig?.enableQuotation === "Y" },
  ];

  const storedParkedList = useSessionStore.getState().parkedList;
  const initialParkedList = storedParkedList ? storedParkedList : [];

  const setDefaultImage = (e) => {
    e.target.src = DefaultProductImage;
  };

  const toCamelCase = (str) => {
    if (!str) return "";
    return str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
      .replace(/\s+/g, "");
  };

  // ═══════════════════════════════════════════════════════════════════
  // CENTRALIZED STATE
  // ═══════════════════════════════════════════════════════════════════
  const coreState = useCoreState({ tillData, defaultCustomer, userData, tillValue, tillDocumentSequence, posConfig });
  // Only destructure variables that are actively referenced in this file (hooks, JSX, effects).
  // All other coreState values are passed to child components via ...coreState spread.
  const {
    RangePicker,
    displayClock,
    setDisplayClock,
    orderHistoryInput,
    setOrderHistoryInput,
    layoutName,
    setLayoutName,
    loading,
    setLoading,
    // detailsLoading,
    // setDetailsLoading,
    setFilterDrawer,
    setFiltersFlag,
    filteredDate,
    setFilterdDate,
    searchHistoryInput,
    setSearchhistoryInput,
    notesValue,
    setNotesValue,
    exchangeNotesValue,
    setExchangeNotesValue,
    activeCartTable,
    setActiveCartTable,
    setSelectedDate,
    selectedProductForNotes,
    setSelectedProductForNotes,
    open,
    setOpen,
    productStock,
    setProductStock,
    showCompleteSaleStock,
    setShowCompleteSaleStock,
    completeSaleStockData,
    setCompleteSaleStockData,
    setStockList,
    setSaleTypeProducts,
    setShowProducts,
    productSearchInput,
    setProductSearchInput,
    productsCopy,
    currenciesData,
    setCurrenciesData,
    selectedDocumentType,
    setSelectedDocumentType,
    setTableCards,
    keyboardInputFields,
    setKeyboardInputFields,
    setKeyValue,
    setIsCardPaymentLoading,
    setCardPaymnetError,
    setCardPaymnetStatus,
    amount,
    setAmount,
    overPayedAmount,
    setOverPayedAmount,
    setPaytmQrCodeModalOpens,
    setQrCodeResponse,
    setCardPaymnetStatusPLB,
    setShowCardPaymnteModal,
    setIntegratedPaymentStatus,
    setMetaData,
    setCustomMixItems,
    setOptions,
    setShowCustomMix,
    setGroupQty,
    displayAddOnSelection,
    setDisplayAddOnSelection,
    displayBatchSelection,
    setDisplayBatchSelection,
    batchSetAvailable,
    setBatchSetAvailable,
    setIsStockModal,
    setTempCart,
    setDisplayManualQtyWeightInput,
    managementScreenShow,
    setManagementScreenShow,
    setSideMenuDrawervisible,
    numb,
    setNumb,
    selectedExternalCouponType,
    orderTypeSelection,
    loader,
    setLoader,
    setIsSaleTypeFlag,
    isCardPaymentFlag,
    setIsCardPaymentFlag,
    selectedItemsModalOpen,
    setIsparkedBill,
    dateRange,
    setDateRange,
    isPopoverVisible,
    setPopoverVisible,
    showCouponOtpPopup,
    setShowCouponOtpPopup,
    couponOtp,
    setCouponOtp,
    couponOtpError,
    setCouponOtpError,
    couponResendTimer,
    setCouponResendTimer,
    couponCanResend,
    setCouponCanResend,
    verificationCouponStatus,
    setVerificationCouponStatus,
    isCallCenterFlow,
    setCallCenterVisible,
    setIsCallCenterFlow,
    setShowCallCenterPage,
    holdQueue,
    showAddonsCustomizePopup,
    setShowAddonsCustomizePopUp,
    addonSourceItem,
    setAddonSourceItem,
    addonRepeatDisabled,
    setAddonRepeatDisabled,
    notesPopup,
    setNotesPopup,
    rejectQuotation,
    setRejectQuotation,
    modalEditState,
    setDynamicForm,
    orderType,
    setOrderType,
    setDisplayOrderType,
    setPosSaleTypes,
    setOrderTimeDetails,
    exchangeCart,
    setExchangeCart,
    splitCarts,
    setOrderObj,
    selectedSaleType,
    selectedSaleTypeData,
    setSelectedSaleTypeData,
    setSelectedSaleType,
    documentSequence,
    setDocumnetSequence,
    form,
    guestForm,
    additionalInfoForm,
    notesInputRef,
    returnProcessRef,
    paymentModalInputRef,
    metaDataRef,
    redeemNumberRef,
    loyaltyInputRef,
    productSearchInputRef,
    keyboardRef,
    searchInputRef,
    qtyInputRef,
    keyboard,
    activeInput,
    setActiveInput,
    giftCardDataRef,
    selectedPaymentMethodRef,
    addProductRef,
    posLogActivityRef,
    fetchOnlineBatchesRef,
    hasShownCustomerModalRef,
    cartRef,
    sendWebHookDataRef,
    handleLoyaltyPaymentRef,
    handleDeleteLoyaltyPaymentRef,
    handleSelectedSaleTypeForProductsRef,
    processBillDiscountsRef,
    setOpenSalesRepRef,
    verifyStockRef,
    handleWalletPostPaymentRef,
    handleGiftCardTopupPostPaymentRef,
    showCustomerWalletRef,
    showGiftCardTopupRef,
    productBOMStock,
    setProductBOMStock,
    productBOMStockData,
    setProductBOMStockData,
    // IRN (B2B e-Invoice) state
    showIRNModal,
    setShowIRNModal,
    irnStatus,
    setIrnStatus,
    irnNumber,
    setIrnNumber,
    irnPdfUrl,
    setIrnPdfUrl,
    irnLoading,
    setIrnLoading,
    completedOrderId,
    setCompletedOrderId,
    invoiceProcessing,
    setInvoiceProcessing,
    invoicePrinted,
    setInvoicePrinted,
    irnGenerating,
    setIrnGenerating,
    irnDownloadReady,
    setIrnDownloadReady,
    pendingIRNOrderId,
    setPendingIRNOrderId,
    irnFailureMessage,
    setIrnFailureMessage,
  } = coreState;

  // Clock effect
  useEffect(() => {
    const timerId = setInterval(() => setDisplayClock(clock()), 1000);
    return () => {
      clearInterval(timerId);
    };
  }, []);

  // One-time migration: seed selectedRevenueCategoryId into the store from localStorage
  // if the store has no value yet (e.g. first launch after this field was added).
  useEffect(() => {
    if (!useTransactionStore.getState().selectedRevenueCategoryId) {
      try {
        const stored = localStorage.getItem("selectedRevenueCategory");
        if (stored) {
          const category = JSON.parse(stored);
          if (category?.revenueCategoryId) {
            useTransactionStore.getState().setSelectedRevenueCategoryId(category.revenueCategoryId);
          }
        }
      } catch (err) {
        console.error("Revenue category migration failed:", err);
      }
    }
  }, []);

  // One-time self-heal: if userData is missing when POS mounts (e.g. wiped by
  // the appVersionGate version-bump bug on a prior deploy, or any other gap
  // that clears it while the till session itself stays open), re-fetch it the
  // same way SignIn.check()/Login.onLogin()/Till.getTillData() already do.
  // Those three only run on /sign-in, /till, or the login action — none of
  // them fire for a session that's already open and lands straight on /pos,
  // so without this an affected till needed an explicit logout/login to
  // recover. No-op on every healthy boot (just a store read).
  useEffect(() => {
    if (!useAuthStore.getState().userData?.user_id) {
      (async () => {
        try {
          const { getGlobalParameters } = await import("../../../services/generic");
          const gpRes = await getGlobalParameters();
          if (gpRes?.status === 200) {
            const parsed = JSON.parse(gpRes.data.data.getGlobalParameters);
            useAuthStore.getState().setUserData(parsed.User);
          }
        } catch (err) {
          console.error("[POS] userData self-heal failed:", err);
        }
      })();
    }
  }, []);

  // DEFAULT CART — sourced from Zustand useCartStore
  const cart = useCartStore((s) => s.cart);
  const setCart = useCartStore((s) => s.setCart);

  const changeOrderType = (type) => {
    setDisplayOrderType(false);
    setOrderType(type);
  };

  // Grouped fields for customer form
  const groupedFields = (() => {
    const grouped = retailCustomerGroups
      .filter((group) => group.tabId === coreState.activeTab && group.visible)
      .sort((a, b) => a.order - b.order)
      .map((group) => {
        const fieldsInGroup = retailCustomerFields
          .filter((field) => {
            const isVisible = field.visible;
            const isCurrentTab = field.tabId === coreState.activeTab;
            const isBusinessTab = coreState.activeTabName?.toLowerCase() === "business";
            const allowBusinessFields = !isBusinessTab || (isBusinessTab && (customerOps?.isB2BEnabled ?? false));
            const isInGroup = field.groupId === group.id;
            return isVisible && isCurrentTab && allowBusinessFields && isInGroup;
          })
          .sort((a, b) => a.order - b.order);
        return {
          groupId: group.id,
          groupName: group.name || "",
          fields: fieldsInGroup,
          collapsible: group.collapsible,
          defaultExpanded: group.defaultExpanded,
          description: group.description,
        };
      });
    const ungroupedFields = retailCustomerFields
      .filter((field) => {
        const isVisible = field.visible;
        const isCurrentTab = field.tabId === coreState.activeTab;
        const isBusinessTab = coreState.activeTabName?.toLowerCase() === "business";
        const allowBusinessFields = !isBusinessTab || (isBusinessTab && (customerOps?.isB2BEnabled ?? false));
        const isUngrouped = !field.groupId;
        return isVisible && isCurrentTab && allowBusinessFields && isUngrouped;
      })
      .sort((a, b) => a.order - b.order);
    if (ungroupedFields.length > 0) {
      grouped.unshift({
        groupId: "",
        groupName: "",
        fields: ungroupedFields,
        collapsible: false,
        defaultExpanded: true,
        description: "",
      });
    }
    return grouped.filter((group) => group.fields.length > 0);
  })();

  // ═══════════════════════════════════════════════════════════════════
  // HOOK INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════

  // --- useCashManagement ---
  const cashManagement = useCashManagement({
    tillData,
    db,
    upsertPOSLog,
    dispatchEvent,
  });
  const {
    cashAddInFlag,
    setCashAddInFlag,
    cashManagementForm,
    cashIn,
    setCashIn,
    cashOut,
    setCashOut,
    pettyCashIn,
    setPettyCashIn,
    pettyCashOut,
    setPettyCashOut,
    bankTransfer,
    setBankTransfer,
    addCashFlag,
    setAddCashFlag,
    fileInputRef,
    handleFileUpload,
    uploadedFileUrl,
    uploadedFileName,
    setUploadedFileUrl,
    setUploadedFileName,
    uploading,
    handleCashInOut,
    handleCahInOut,
    onChangeCheckbox,
  } = cashManagement;

  // --- Revenue Category ---
  const [isRevenueCategoryFlag, setIsRevenueCategoryFlag] = useState(false);
  const [selectedRevenueCategoryName, setSelectedRevenueCategoryName] = useState(() => {
    try {
      const stored = localStorage.getItem("selectedRevenueCategory");
      return stored ? JSON.parse(stored)?.name || "" : "";
    } catch {
      return "";
    }
  });
  const [revenueCategoryList, setRevenueCategoryList] = useState([]);
  const [isNewGiftCardFlag, setIsNewGiftCardFlag] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({});
  const [giftCardFlag, setGiftCardFlag] = useState(false);
  const handleSelectedRevenueCategory = (category) => {
    localStorage.setItem("selectedRevenueCategory", JSON.stringify(category));
    useTransactionStore.getState().setSelectedRevenueCategoryId(category?.revenueCategoryId || null);
    setSelectedRevenueCategoryName(category?.name || "");
    setIsRevenueCategoryFlag(false);
  };

  const openRevenueCategoryDrawer = async () => {
    try {
      const categories = await db.getRevenueCategories.toArray();
      setRevenueCategoryList(categories || []);
    } catch {
      setRevenueCategoryList([]);
    }
    setIsRevenueCategoryFlag(true);
  };

  // --- useCartOperations ---
  const cartOperations = useCartOperations({
    cart,
    setCart,
    addProduct: (...args) => addProductRef.current?.(...args),
    tillData,
    orderType,
    posConfig,
    precision,
    taxIncludeFlag,
    isExchange,
    searchInputRef,
    selectedSaleTypeData,
    defaultCustomer,
    giftCardData: giftCardDataRef.current,
    setGiftCardData: (v) => {
      if (giftCard) giftCard.setGiftCardData?.(v);
      giftCardDataRef.current = v;
    },
    posLogActivity: (...args) => posLogActivityRef.current?.(...args),
    documentSequence,
    setAmount: (v) => {
      if (paymentFlow) paymentFlow.setAmount?.(v);
    },
    setSelectedPaymentMethod: (v) => {
      if (paymentFlow) paymentFlow.setSelectedPaymentMethod?.(v);
      selectedPaymentMethodRef.current = v;
    },
    selectedReturnType: useTransactionStore.getState().selectedReturnType || "Refund",
    setDisplayManualQtyWeightInput,
    setAddOnsList: (v) => {
      if (productAddons) productAddons.setAddOnsList?.(v);
    },
    setSelectedProduct: (v) => {
      if (productAddons) productAddons.setSelectedProduct?.(v);
    },
    setAddonSourceItem,
    setShowAddonsCustomizePopUp,
    setDisplayBatchSelection,
    selectedPaymentMethod: selectedPaymentMethodRef.current,
    processPayment: (...args) => {
      if (paymentFlow) return paymentFlow.processPayment?.(...args);
    },
  });
  const {
    isQtyUpdate,
    setIsQtyUpdate,
    showPaymentMethods,
    setShowPaymentMethods,
    selectedProductInCart,
    setSelectedProductInCart,
    selectedProductQty,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedKeys,
    setSelectedKeys,
    isInputFocused,
    setIsInputFocused,
    inputFocused,
    setInputFocused,
    realPrice: realPriceFromCart,
    setRealPrice: setRealPriceFromCart,
    firstInputValue: firstInputValueFromCart,
    setFirstInputValue: setFirstInputValueFromCart,
    qtyNumberFlag,
    setQtyNumberFlag,
    manualDiscountModalVisible,
    setManualDiscountModalVisible,
    manualDiscountInput,
    setManualDiscountInput,
    manualDiscountTypes,
    setManualDiscountTypes,
    selectedManualDiscountType,
    setSelectedManualDiscountTypeValue,
    enableManualDiscountInput,
    setEnableManualDiscountInput,
    manualDiscountForm,
    totalBillDiscountData,
    setTotalBillDiscountData,
    discountAdditionalInfo,
    setDiscountAdditionalInfo,
    discountVerificationData,
    setDiscountVerifictionData,
    discountAdditionalInfoForm,
    selectedManualDiscountTypeValueState,
    setSelectedManualDiscountTypeValueState,
    selectProductInCart,
    clearSelectedProductInCart,
    enterTotalQty,
    enterRealPrice,
    onChangeTotalQuantity,
    handleTotalQty,
    handleRealPrice,
    deleteProduct,
    decreaseProductQty,
    increaseProductQty,
    deleteReceipt,
    deleteCart,
    applyManualDiscount,
    processLineManualDiscount,
    processTotalManualDiscount,
    removeAllDiscounts,
    processManualDiscount,
  } = cartOperations;

  const selectProduct = (record) => {
    setSelectedKeys([record.key]);
  };
  const selectSalseProduct = (record) => {
    setSelectedKeys([record.sOrderID]);
  };

  // --- usePaymentFlow ---
  const paymentFlow = usePaymentFlow({
    cart,
    setCart,
    tillData,
    tillDataPaymentMethods,
    posConfig,
    selectedSaleTypeData,
    precision,
    orderType,
    addProduct: (...args) => addProductRef.current?.(...args),
    pricePrecision,
    amount,
    setAmount,
    overPayedAmount,
    setOverPayedAmount,
    numb,
    setNumb,
    selectedProductInCart,
    setSelectedProductInCart,
    setShowPaymentMethods,
    showPaymentMethods,
    giftCardData: giftCardDataRef.current,
    setGiftCardData: (v) => {
      if (giftCard) giftCard.setGiftCardData?.(v);
      giftCardDataRef.current = v;
    },
    paymentModalInputRef,
    isExchange,
    CWHardwareController,
    setPaytmQrCodeModalOpens,
    setQrCodeResponse,
    setLoading,
    setLayoutType: (v) => {
      if (customerOps) customerOps.setLayoutType?.(v);
    },
    setKioskUI: (v) => {
      if (customerOps) customerOps.setKioskUI?.(v);
    },
    dispatchQRCodeEvent,
    closeQRCodeModal,
    dispatchCCAvenueEvent,
    closeCCAvenueModal,
    exchangeCart,
    setExchangeCart,
    processOrder: (...args) => {
      if (orderProcessing) return orderProcessing.processOrder?.(...args);
    },
    selectedDocumentType,
    setSelectedDocumentType,
    setLoader,
    tillLayout,
    setIsCardPaymentLoading,
    isCardPaymentFlag,
    setIsCardPaymentFlag,
    setCardPaymnetError,
    setCardPaymnetStatus,
    setCardPaymnetStatusPLB,
    setShowCardPaymnteModal,
    dispatchEvent,
    retailCustomerRules,
    redeemNumberRef,
    loyaltyInputRef,
    setIsInputFocused,
    searchInputRef,
    setIntegratedPaymentStatus,
    setOpen,
    setCustomMixItems,
    // Wallet/GiftCard topup flow — lazy refs, resolved after hooks init
    showCustomerWalletRef,
    handleWalletPostPaymentRef,
    isNewGiftCardFlagFromParent: isNewGiftCardFlag, // uses internal isNewGiftCardFlag
    showGiftCardTopupRef,
    handleGiftCardTopupPostPaymentRef,
    setIsNewGiftCardFlag,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    giftCardFlag,
    setGiftCardFlag,
  });
  const {
    // selectedPaymentMethod,
    // setSelectedPaymentMethod,
    denaminationsKeyboard,
    setDenaminationsKeyboard,
    paymentProcessFlag,
    setPaymentProcessFlag,
    isShowingAll,
    setIsShowingAll,
    showPayments,
    setShowPayments,
    startIndex,
    setStartIndex,
    filteredPaymentMethods,
    loyaltyPaymentOtp,
    setLoyaltyPaymentOtp,
    loyaltyInputValue,
    setLoyaltyInputValue,
    loyalityOtpModalVisible,
    setLoyalityOtpModalVisible,
    loyalityOtpData,
    setLoyalityOtpData,
    paymentModalLoyalityMessages,
    setPaymentModalLoyalityMessages,
    paymentModalByCustomerState,
    setPaymentModalByCustomerState,
    // giftCardFlag,
    // setGiftCardFlag,
    isGiftCardFlag,
    setIsGiftCardFlag,
    isWalletFlag,
    setIswalletFlag,
    giftCardMode,
    setGiftCardMode,
    initialShowPayments,
    paymentModal,
    setPaymentModalLocal,
    handleCartTotalClick,
    filterPaymentsBySaleType,
    getFilteredPaymentMethods,
    handlePayment,
    isValidAmount,
    handleDeletePayment,
    requestPayment,
    processPayment,
    onChangeAmount,
    handleAmount,
    handleCashPayment,
    showConfirm,
    openPaymentModal,
    openPaymentModalByCustomer,
    closePaymentModal,
    processOtpInput,
    handleLoyalityInput,
    checkLoyality,
  } = paymentFlow;

  selectedPaymentMethodRef.current = selectedPaymentMethod;

  // --- useGiftCard ---
  const giftCard = useGiftCard({
    cart,
    setCart,
    tillData,
    selectedPaymentMethod,
    precision,
    isExchange,
    upsertPOSLog,
    authHeaders,
    db,
    addProduct: (...args) => addProductRef.current?.(...args),
    requestPayment,
    giftCardFlag,
    setGiftCardFlag,
  });
  const {
    giftCardForm,
    giftCardItems,
    setGiftCardItems,
    giftCardType,
    setGiftCardType,
    selectGiftCardItem,
    setSelectGiftCardItem,
    validateGiftCard,
    setValidateGiftCard,
    giftCardData,
    setGiftCardData,
    giftCardBalance,
    setGiftCardBalance,
    validateGiftCardForm,
    isSubmitting,
    setIsSubmitting,
    handleGiftCardDetails,
    redeemGiftCard,
    handleGiftCard,
    checkGiftCardBalance,
  } = giftCard;

  giftCardDataRef.current = giftCardData;

  // --- useOrderHistory ---
  const orderHistory = useOrderHistory({
    tillData,
    authHeaders,
    precision,
    taxIncludeFlag,
    tillDataPaymentMethods,
    posConfig,
    cart,
    searchHistoryInput,
    setSearchhistoryInput,
    filteredDate,
    setFilterdDate,
    setFiltersFlag,
    metaDataRef,
    setMetaData,
    loading,
    setLoading,
    // detailsLoading,
    // setDetailsLoading,
  });
  const {
    displayOrderHistory,
    setDisplayOrderHistory,
    orderHistoryDetails,
    setOrderHistoryDetails,
    ordersCopy,
    setOrdersCopy,
    selectDate,
    setSelectDate,
    salesHistoryType,
    setSalesHistoryType,
    startRowData,
    setStartRowData,
    orderHistorySearchType,
    setOrderHistorySearchType,
    selectedOrderHistoryLine,
    setSelectedOrderHistoryLine,
    orderHistoryInput: orderHistoryInputFromHook,
    setOrderHistoryInput: setOrderHistoryInputFromHook,
    prevHistoryRef,
    changeOrderHistorySearchType,
    showOrderHistory,
    showOrderHistoryLine,
    getSalesHistoryData,
    searchOrderHistory,
    // loading: salesHistoryLoading,
  } = orderHistory;

  const getDisplayDate = () => {
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      // RangePicker has allowEmpty — one side can be null after a partial pick.
      if (!start || !end) return "Choose date range";
      return `${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")}`;
    }
    return "Choose date range";
  };

  // Shown in the calendar when the popover opens (prefilled with the currently applied
  // range, so the user sees what's active). Deliberately UNCONTROLLED (defaultValue, no
  // value prop) — a controlled value re-synced on every onCalendarChange fights rc-picker's
  // own focus-auto-advance (rc-picker calls selectorRef.focus() internally on each click,
  // then our own re-render with a fresh controlled value undoes it — confirmed by reading
  // rc-picker's RangePicker.js triggerPartConfirm). pickerSessionKey is bumped ONLY on
  // popover open, forcing a full remount so rc-picker's own internal bookkeeping
  // (useRangeActive's activeListRef — which side has genuinely been visited this
  // session) starts fresh every time. That native mechanism already guarantees the
  // outer onChange only fires once the user has genuinely clicked both the start and
  // end fields — it does NOT fire just because the merged calendarValue momentarily
  // looks complete (e.g. one fresh click + one stale leftover side). An earlier version
  // of this file re-implemented that same "both sides genuinely touched" gate by hand
  // (a discard-and-remount on the first click), but remounting mid-session — while a
  // click on the old DOM was still being processed — raced with the Popover's
  // outside-click detection and intermittently closed the popover after a single
  // click. Trusting rc-picker's native gate and remounting only at the safe, pre-click
  // boundary (popover open) removes that race entirely.
  const [pickerDefaultValue, setPickerDefaultValue] = useState([null, null]);
  const [pickerSessionKey, setPickerSessionKey] = useState(0);
  useEffect(() => {
    if (isPopoverVisible) {
      setPickerDefaultValue(dateRange && dateRange.length === 2 ? dateRange : [null, null]);
      setPickerSessionKey((k) => k + 1);
    }
  }, [isPopoverVisible]);

  const content = (
    <RangePicker
      key={pickerSessionKey}
      open={isPopoverVisible}
      allowEmpty
      id="sm-salesHistory-date"
      onPressEnter={searchOrderHistory}
      allowClear={true}
      format={"YYYY-MM-DD"}
      suffixIcon={null}
      separator={null}
      defaultValue={pickerDefaultValue}
      // Force calendar popup to render inside parent so click events stay scoped
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      onCalendarChange={() => {
        // No-op. rc-picker's own bookkeeping (useRangeActive's activeListRef +
        // nextActiveIndex) already guarantees onChange below only fires once the user
        // has genuinely clicked both the start and end fields this session.
      }}
      onChange={(e) => {
        if (e === null) {
          // Cleared — don't close the popover; user may want to pick a fresh range.
          setFilterdDate(null);
          setDateRange([]);
          setOrderHistoryDetails([]);
          localStorage.setItem("dataLength", 0);
          if (searchHistoryInput !== "") {
            searchOrderHistory("customer", searchHistoryInput, 0, false);
          } else {
            searchOrderHistory("customer", "", 0, false);
          }
        } else if (e && e[0] && e[1]) {
          // Both sides genuinely picked (allowEmpty can otherwise let a single date
          // count as "done" — reject that here so a lone pick never auto-applies).
          setPopoverVisible(false);
          setFilterdDate(null);
          setDateRange(e);
          setOrderHistoryDetails([]);
          localStorage.setItem("dataLength", 0);
          searchOrderHistory("orderDateSearchKey", e, 0, searchHistoryInput !== "" ? true : false, false);
        }
        // else: partial single-side pick — leave the now-uncontrolled RangePicker to
        // manage its own display; there's no controlled value left to sync.
      }}
      className="salesHistory-datePicker-sm"
    />
  );

  // --- useKeyboardHandlers ---
  const keyboardHandlers = useKeyboardHandlers({
    searchParkedBill: () => parkedBills?.searchParkedBill?.(),
    searchOrderHistory,
    setProductSearchInput,
    clearProductSearchResults: () => productOps?.clearProductSearchResults?.(),
    setParkedBillSearchInput: (v) => parkedBills?.setParkedBillSearchInput?.(v),
    setOrderHistoryInput,
  });
  const {
    keyboardType,
    setKeyboardType,
    layout,
    setLayout,
    inputName,
    setInputName,
    keyboardProduct,
    keyboardParkbill,
    orderHistorySearchInputRef,
    handleKeyPress,
    handleKeyboardInput,
  } = keyboardHandlers;

  // --- useParkedBills ---
  const parkedBills = useParkedBills({
    cart,
    setCart,
    deleteCart,
    deleteReceipt,
    orderType,
    giftCardData,
    setGiftCardData,
    documentSequence,
    setDocumnetSequence,
    tillData,
    upsertPOSLog,
    posConfig,
    authHeaders,
    getFilteredPaymentMethods,
    setShowPayments,
    setSelectedDocumentType,
    setProductSearchInput,
    setOpen,
    searchInputRef,
    setShowPaymentMethods,
    setOrderHistoryDetails,
    setSelectedProductInCart,
    setProductStock,
    setTempCart,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
    setManagementScreenShow,
    setSelectedSaleType,
    setKeyValue,
    handleSelectedSaleTypeForProducts: (...args) => handleSelectedSaleTypeForProductsRef.current?.(...args),
    handleLoyaltyPayment: (...args) => handleLoyaltyPaymentRef.current?.(...args),
    hasShownCustomerModalRef,
    isExchange,
    setIsparkedBill,
    tillaccess,
    metaDataRef,
    history,
  });
  const {
    displayParkedBillModal,
    setDisplayParkedBillModal,
    parkedList,
    setParkedList,
    filterdParkedList,
    setFilterdParkedList,
    parkedBillSearchInput,
    setParkedBillSearchInput,
    openDisplayParkedBillModal,
    parkBill,
    selectParkedBill,
    selectLayAwayOrder,
    discardParkedBill,
    retrieveParkedData,
    discardGiftCards,
    processWithParkedBillCancellation,
    searchParkedBill,
    handleParkedBillSearchInput,
    mapParkedList,
    closeParkedBillModal,
    discardMessage,
    handleOk,
    handleCancel,
  } = parkedBills;

  // --- useSalesReturn ---
  const salesReturn = useSalesReturn({
    cart,
    setCart,
    tillData,
    precision,
    posConfig,
    isExchange,
    loading,
    setLoading,
    setKeyValue,
    giftCardItems,
    setSideMenuDrawervisible,
  });
  const {
    selectedReturnOrder,
    setSelectedReturnOrder,
    dataSource,
    setDataSource,
    checkedKeys,
    setCheckedKeys,
    retrunSearchType,
    setReturnSearchType,
    showReturnPurchase,
    setShowReturnPurchase,
    showReturnOptions,
    setShowReturnOptions,
    isBlindReceiptPending,
    setIsBlindReceiptPending,
    isPurchaseReturn,
    setIsPurchaseReturn,
    selectedReturnType,
    setSelectedReturnType,
    displayReturnOrderSearch,
    setDisplayReturnOrderSearch,
    returnProcessRef: returnProcessRefFromHook,
    handleSalesReturnFromSideMenu,
    selectPurchaseBillForReturn,
    searchReturnHistory,
    getProductData,
    handleSaveReturnCartRecord,
    addSelectedReturnProductsToCart,
  } = salesReturn;

  // --- useCustomerOperations ---
  const customerOps = useCustomerOperations({
    cart,
    setCart,
    form,
    tillData,
    tillLayout,
    pricingRuleController,
    addProduct: (...args) => addProductRef.current?.(...args),
    removeAllDiscounts,
    posConfig,
    precision,
    taxIncludeFlag,
    orderType,
    cartRef,
    manualDiscountTypes,
    processBillDiscounts: (...args) => processBillDiscountsRef.current?.(...args),
    openPaymentModalByCustomer,
    defaultCustomer,
    isExchange,
    searchInputRef,
    selectedSaleTypeData,
    handleDeleteLoyaltyPayment: (...args) => handleDeleteLoyaltyPaymentRef.current?.(...args),
    hasShownCustomerModalRef,
    setShowPaymentMethods,
    verifyStock: (...args) => verifyStockRef.current?.(...args),
    posLogActivity: (...args) => posLogActivityRef.current?.(...args),
    isCallCenterFlow,
    setCallCenterVisible,
    setIsCallCenterFlow,
    setShowCallCenterPage,
    guestForm,
    history,
    setActiveInput,
    setIsInputFocused,
    groupedFields,
    toCamelCase,
    retailCustomerFields,
    retailCustomerTabs,
    customerConfigData,
    keyboardInputFields,
    setKeyboardInputFields,
    setLoading,
  });
  const {
    displayCustomerSearch,
    setDisplayCustomerSearch,
    displayUAECustomerSearch,
    setDisplayUAECustomerSearch,
    displayUAECustomer,
    setDisplayUAECustomer,
    customerSearchType,
    setCustomerSearchType,
    customerSearchInput,
    setCustomerSearchInput,
    customerSearchResults,
    setCustomerSearchResults,
    closeCustomerFlag,
    setCloseCustomerFlag,
    displayAddNewCustomer,
    setDisplayAddNewCustomer,
    displayEditOldCustomer,
    setDisplayEditOldCustomer,
    selectedEditOldCustomer,
    setSelectedEditOldCustomer,
    customerFlag,
    setCustomerFlag,
    isPanUpdateMode,
    setIsPanUpdateMode,
    isAddNewCustomer,
    setIsAddNewCustomer,
    uploadedDocuments,
    setUploadedDocuments,
    uploadModalVisible,
    setUploadModalVisible,
    isCreditSale,
    setIsCreditSale,
    isB2BEnabled,
    setIsB2BEnabled,
    selectedB2BCustomer,
    setSelectedB2BCustomer,
    switchToggle,
    setSwitchToggle,
    formDataCache,
    setFormDataCache,
    isViewMode,
    setIsViewMode,
    viewCustomerValues,
    setViewCustomerValues,
    isSubmittingCustomer,
    setIsSubmittingCustomer,
    isCustomerDrawerOpen,
    setIsCustomerDrawerOpen,
    expandedGroups,
    setExpandedGroups,
    kioskUI,
    setKioskUI,
    kioskLogin,
    layoutType,
    setLayoutType,
    pendingRefundCustomer,
    setPendingRefundCustomer,
    isRefundCustomerEdit,
    setIsRefundCustomerEdit,
    isRefundWalletOpen,
    setIsRefundWalletOpen,
    activeTab,
    setActiveTab,
    activeTabName,
    setActiveTabName,
    UAECustomerForm,
    customerForm,
    customerSearchInputRef,
    closeCustomerSearch,
    handleCustomerSearch,
    proceedWithCustomerSelection,
    selectCustomer,
    showAddNewCustomerFields,
    showAddNewUAECustomerFields,
    addNewCustomer,
    showEditOldCustomerFields,
    editOldCustomer,
    removeCutomer,
    validateAndSavePanForCustomer,
    extractPanFromMeta,
    getPanRealStatus,
    autoEnablePanCustomer,
    autoEnableNriBilling,
    convertMetaDataToKeyValue,
    toCamelCaseMetaData,
    normalizeStaticSelectValue,
    isPanCustomer,
    setIsPanCustomer,
    panCustomerInfo,
    setPanCustomerInfo,
    isNriBilling,
    setIsNriBilling,
    panDrawerMode,
    setPanDrawerMode,
    documents,
    setDocuments,
    customerSearchTrigger,
    setCustomerSearchTrigger,
    customerLoading,
    setCustomerLoading,
    lastSearchedValue,
    setLastSearchedValue,
  } = customerOps;

  // --- useCouponProcessing ---
  const couponProcessing = useCouponProcessing({
    cart,
    setCart,
    tillData,
    precision,
    posConfig,
    orderType,
    cartRef,
    isExchange,
    taxIncludeFlag,
    processBillDiscounts: (...args) => processBillDiscountsRef.current?.(...args),
    removeAllDiscounts,
    searchInputRef,
    setIsInputFocused,
    selectedSaleTypeData,
    defaultCustomer,
    openPaymentModalByCustomer,
    discountApproval,
    totalBillDiscountData,
    selectedExternalCouponType,
    additionalInfoForm,
  });
  const {
    couponModalVisible,
    setCouponModalVisible,
    couponInput,
    setCouponInput,
    couponVerificationData: couponVerificationDataFromHook,
    setCouponVerificationData: setCouponVerificationDataFromHook,
    showCouponApproval,
    setShowCouponApproval,
    couponApprovals,
    setCouponApprovals,
    selectedCoupon,
    setSelectedCoupon,
    totalBillCouponData,
    setTotalBillCouponData,
    showAdditionalInfo,
    setShowAdditionalInfo,
    additionalInfo,
    setAdditionalInfo,
    couponInputRef,
    closeCouponModal,
    handleCouponInput,
    processCouponApplication,
    couponInfo,
    checkExternalCoupon,
    checkCoupon,
    removeCoupon,
  } = couponProcessing;

  // --- useProductAddons ---
  const productAddons = useProductAddons({
    cart,
    setCart,
    tillData,
    tillLayout,
    orderType,
    isExchange,
    selectedProductInCart,
    setSelectedProductInCart,
    deleteCart,
    processTotalManualDiscount,
    setLoader,
    salesRepresentDefaultLine: coreState.salesRepresentDefaultLine,
    cartRef,
    productsCopy,
    salesRepresent: coreState.salesRepresent,
    processBillDiscounts: processTotalManualDiscount,
    setSelectedRowKeys,
    setOrderTimeDetails,
    setDisplayAddOnSelection,
    displayAddOnSelection,
    manualDiscountTypes,
    setProductStock,
    setProductBOMStock,
    setProductBOMStockData,
    setTableCards,
    dispatchItemSelection,
    searchInputRef,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
    setManualDiscountInput,
    setSelectedManualDiscountTypeValue,
    removeAllDiscounts,
    setSelectedPaymentMethod,
    selectedReturnType,
    setFilterDrawer,
  });
  const {
    addOnsList,
    setAddOnsList,
    selectedAddons,
    setSelectedAddons,
    isUpdatingAddon,
    setIsUpdatingAddon,
    copyKOTVisible,
    setCopyKOTVisible,
    groupedFiredItems,
    setGroupedFiredItems,
    selectedCopyItems,
    setSelectedCopyItems,
    selectedProduct,
    setSelectedProduct,
    addDefinedProductWithAddons,
    handleAddOnModal,
    handleAddOnValue,
    handleQty,
    handleCopyKOT,
    openCopyKOTModal,
    handleAdd,
    handleAddOnModalClose,
  } = productAddons;

  // --- useOrderProcessing ---
  const orderProcessing = useOrderProcessing({
    cart,
    setCart,
    setSwitchToggle: customerOps.setSwitchToggle,
    tillData,
    tillDataPaymentMethods,
    posConfig,
    precision,
    orderType,
    taxIncludeFlag,
    authHeaders,
    giftCardData,
    setGiftCardData,
    documentSequence,
    setDocumnetSequence,
    deleteCart,
    overPayedAmount,
    setOverPayedAmount,
    amount,
    setAmount,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    setPaymentModal: setPaymentModalLocal,
    setShowPaymentMethods,
    setLoader,
    setLoading,
    splitCarts,
    selectedSaleTypeData,
    setSelectedSaleType,
    setSelectedSaleTypeData,
    setSelectedProductInCart,
    setSelectedRowKeys,
    setStartIndex,
    setCustomMixItems,
    setSelectedDate,
    setSelectedDocumentType,
    setPaymentProcessFlag,
    setNotesValue,
    setIsInputFocused,
    hasShownCustomerModalRef,
    searchInputRef,
    setDisplayCustomerSearch,
    setIsRefundWalletOpen,
    setSelectedCoupon,
    documents,
    setDocuments,
    retailCustomerFields,
    customerForm,
    tillLayout,
    history,
    showOrderHistory,
    setActiveInput,
    eBillConfig: undefined,
    sendWebHookData: (...args) => sendWebHookDataRef.current?.(...args),
    orderTypeSelection,
    isCallCenterFlow,
    pricePrecision,
    initialShowPayments,
    setShowPayments,
    saleTypeData,
    setShowIRNModal,
    setIrnStatus,
    setIrnNumber,
    setIrnPdfUrl,
    setIrnLoading,
    setCompletedOrderId,
    setInvoiceProcessing,
    setInvoicePrinted,
    setIrnGenerating,
    setIrnDownloadReady,
    setPendingIRNOrderId,
    setIrnFailureMessage,
    setProductStock,
    setTempCart,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
  });
  const {
    activeOrderProcess,
    setActiveOrderProcess,
    orderDelay,
    setOrderDelay,
    showPrintTemplateModal,
    setShowPrintTemplateModal,
    selectedPrintTemplate,
    setSelectedPrintTemplate,
    pendingOrderParam,
    setPendingOrderParam,
    pendingDocumentNo,
    setPendingDocumentNo,
    pendingIsSynced,
    setPendingIsSynced,
    isPendingDirectApproved,
    setIsPendingDirectApproved,
    isPendingSyncOrder,
    setIsPendingSyncOrder,
    printTemplates: printTemplatesFromHook,
    processOrder,
    processOrderApproved,
    syncOrders,
    handlePrintTemplateConfirm,
    handlePrintTemplateCancel,
    getDefaultSaleType,
    fetchIRNDetails,
  } = orderProcessing;

  // --- useProductOperations ---
  const productOps = useProductOperations({
    cart,
    setCart,
    tillData,
    tillLayout,
    posConfig,
    precision,
    isExchange,
    authHeaders,
    addProduct: (...args) => addProductRef.current?.(...args),
    setSelectedProductInCart,
    selectedProductInCart,
    setQtyNumberFlag,
    setInputFocused,
    setDisplayBatchSelection,
    setBatchSetAvailable,
    setDisplayManualQtyWeightInput,
    setProductStock,
    setTempCart,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
    setShowPaymentMethods,
    setOptions,
    setActiveInput,
    setNumb,
    paymentModalInputRef,
    productSearchInputRef,
    keyboard,
    searchInputRef,
    setFilterDrawer,
    setLayoutType,
    productsCopy,
    fetchOnlineBatches: (...args) => fetchOnlineBatchesRef.current?.(...args),
    handleCartTotalClick,
    setIsStockModal,
    isNriBilling,
    retailCustomerRules,
    isPanCustomer,
    setPanDrawerMode,
    setSelectedEditOldCustomer,
    convertMetaDataToKeyValue,
    retailCustomerFields,
    customerForm,
    toCamelCase,
    setDocuments,
    setIsPanUpdateMode,
    setDisplayAddNewCustomer,
    hasShownCustomerModalRef,
    setCustomerSearchTrigger,
    setDisplayCustomerSearch,
    setDisplayEditOldCustomer,
    setOpenSalesRep: (...args) => setOpenSalesRepRef.current?.(...args),
    dispatchWeight,
    productSearchInput,
    setProductSearchInput,
    history,
  });
  const {
    productWeightModalInput,
    setProductWeightModalInput,
    currentWeightSelectedProduct,
    setCurrentWeightSelectedProduct,
    isProductsFilter,
    setIsProductsFilter,
    allProductCategories,
    setAllProductCategories,
    selectedProductBrand,
    setSelectedProductBrand,
    selectedProductCategory,
    selectCategotyList,
    setSelectCategotyList,
    productsData,
    setProductsData,
    prevProductsListRef,
    isSearchProducts,
    setIsSearchProducts,
    isProductsVisible,
    setIsProductsVisible,
    productListCardRef,
    showManualBatchPopup,
    setShowManualBatchPopup,
    manualBatchNo,
    setManualBatchNo,
    manualBatchProduct,
    addManualBatchProduct,
    onBarcodeInput,
    verifyStock,
    addDefinedProduct,
    selectProductToCart,
    onProductModalChangeWeight,
    handleWeightManual,
    pickProduct,
    checkIsManualWeight,
    modulo,
    addManualWeightToProduct,
    callGetWeightAPI,
    selectProductCategory,
    filterProducts,
    handleBrandCheckboxChange,
    handleCategoryCheckboxChange,
    handleSelectProduct,
    getMoreProducts,
    getCategoryProducts,
    getSearchedProducts,
    kioskFilteredProducts,
    getSearchedItem,
    clearProductSearchResults,
    closeProductPanel,
  } = productOps;

  // Wire verifyStock ref so useCustomerOperations can call it via closure
  verifyStockRef.current = verifyStock;

  // --- useOMSOperations ---
  const omsOps = useOMSOperations({
    tillData,
    setSideMenuDrawervisible,
  });
  const {
    omsOrdersList,
    setOmsOrderList,
    displayOMSOrderItemsModal,
    setDisplayOMSOrderItemsModal,
    selectedOMSOrderStatus,
    setSelectedOMSOrderStatus,
    order,
    setOrder,
    searchOrders,
    setSearchOrders,
    selectedOrder,
    setSelectedOrder,
    omsOrderStatus,
    omsOrderTotalPrice,
    handleOmsOrders,
    handleOMSOrderStatusSelection,
    setOMSStatus,
    handleOMSStatusButton,
    handleOrderCard,
    handleOrderSearchInput,
  } = omsOps;

  // --- useWalletAndBatches ---
  const walletAndBatches = useWalletAndBatches({
    cart,
    tillData,
    posConfig,
    batchSetAvailable,
    setDisplayBatchSelection,
    selectProductToCart,
    setProductStock,
    setGiftCardItems,
    kioskUI: customerOps.kioskUI,
    setKioskUI: (v) => customerOps.setKioskUI?.(v),
    layoutType: customerOps.layoutType,
    setLayoutType: (v) => customerOps.setLayoutType?.(v),
  });
  const {
    showCustomerWallet,
    setShowCustomerWallet,
    customersData,
    setCustomersData,
    selectedCustomer,
    setSelectedCustomer,
    onlineBatches,
    setOnlineBatches,
    addToBagProducts,
    setAddToBagProducts,
    addToBagFlag,
    setAddToBagFlag,
    tillLink,
    setTillLink,
    walletFunc,
    fetchWalletData,
    fetchOnlineBatches,
  } = walletAndBatches;
  fetchOnlineBatchesRef.current = fetchOnlineBatches;
  showCustomerWalletRef.current = showCustomerWallet;

  // --- useCartHandlers ---
  const cartHandlers = useCartHandlers({
    cart,
    setCart,
    tillData,
    posConfig,
    precision,
    orderType,
    isExchange,
    tillLayout,
    cartRef,
    productsCopy,
    salesRepresent: coreState.salesRepresent,
    salesRepresentDefaultLine: coreState.salesRepresentDefaultLine,
    selectedProductInCart,
    setSelectedProductInCart,
    deleteCart,
    processTotalManualDiscount,
    setLoader,
    setSelectedRowKeys,
    setOrderTimeDetails,
    setDisplayAddOnSelection,
    displayAddOnSelection,
    handleAddOnModal,
    manualDiscountTypes,
    setProductStock,
    setProductBOMStock,
    setProductBOMStockData,
    setTableCards,
    dispatchItemSelection,
    searchInputRef,
    setShowCompleteSaleStock,
    setCompleteSaleStockData,
    setManualDiscountInput,
    setSelectedManualDiscountTypeValue,
    removeAllDiscounts,
    setSelectedPaymentMethod,
    selectedReturnType,
    setSelectedAddons,
    setStockList,
    setProductSearchInput,
    setShowPaymentMethods,
    setDynamicForm,
    setDisplayBatchSelection,
    paymentFlow,
    giftCard,
    giftCardDataRef,
    getFilteredPaymentMethods,
    setShowPayments,
    setSelectedDocumentType,
    setOpen,
    setSelectedSaleTypeData,
    setSelectedSaleType,
    setIsSaleTypeFlag,
    setSaleTypeProducts,
    setShowProducts,
    setLoading,
    giftCardData,
    addProductRef,
    processBillDiscountsRef,
    handleSelectedSaleTypeForProductsRef,
    handleLoyaltyPaymentRef,
    handleDeleteLoyaltyPaymentRef,
    receiptlessReturn2Enabled,
    selectedPaymentMethod,
    amount,
    processPayment,
    authHeaders,
    parkBill,
    history,
    setPaymentModalLocal,
    setDisplayOfferProductSelection: undefined,
    setOfferProductList: undefined,
    dispatchEvent,
    setOverPayedAmount,
    setGroupQty,
    setKeyValue,
    setCustomMixItems,
    setShowCustomMix,
    setIsInputFocused,
    removeCoupon,
  });
  const {
    addProduct,
    submitCustomAttributs,
    modifyProductList,
    processBillDiscounts,
    handleSelectedSaleTypeForProducts,
    clearReturnCartData,
    adjustDiscount,
    handleLoyaltyPayment,
    handleDeleteLoyaltyPayment,
    selectOfferProduct,
    handleVerifyPayment,
    confirmDiscardCart,
    changeCustomerId,
    offerProductsList,
    setOfferProductList,
    displayOfferProductSelectiton,
    setDisplayOfferProductSelection,
  } = cartHandlers;

  const keyboardLayoutType = tillLayout === 2 ? "restaurant" : "supermarket";
  const zoneNavigation = useZoneNavigation({
    layout: keyboardLayoutType,
    enabled: posConfig?.enableKeyboardNavigation === "Y",
    zoneRefs: {},
  });

  // --- useCoreEffects ---
  const coreEffects = useCoreEffects({
    cart,
    setCart,
    isExchange,
    precision,
    tillData,
    tillLayout,
    posConfig,
    history,
    tillaccess,
    notesPopup,
    notesValue,
    setNotesValue,
    exchangeNotesValue,
    setExchangeNotesValue,
    activeCartTable,
    setActiveCartTable,
    selectedProductForNotes,
    setSelectedProductForNotes,
    setNotesPopup,
    setIsInputFocused,
    keyboardRef,
    layoutName,
    setLayoutName,
    setOverPayedAmount,
    setSelectedProductInCart,
    setGroupQty,
    showPaymentMethods,
    setShowPaymentMethods,
    setPaymentProcessFlag,
    getFilteredPaymentMethods,
    setShowPayments,
    setStartIndex,
    amount,
    selectedPaymentMethod,
    isNewGiftCardFlag,
    giftCardMode,
    setGiftCardMode,
    showCustomerWallet,
    processOrder,
    activeInput,
    searchInputRef,
    qtyInputRef,
    productStock,
    setProductStock,
    currenciesData,
    setCurrenciesData,
    setPosSaleTypes,
    setOrderType,
    holdQueue,
    activeTabName,
    setActiveTabName,
    retailCustomerFields,
    selectedSaleType,
    isRefundWalletOpen,
    setSelectedPaymentMethod,
    setAmount,
    setOrderObj,
    suppressAutoFocusRef: zoneNavigation.suppressAutoFocusRef,
    setDocumnetSequence,
  });
  const { onAddNotes, handleKeyDown, onChangeInputKeyboard, onChange, handleShift } = coreEffects;

  // --- useEBillWebhook ---
  const eBillWebhook = useEBillWebhook({ tillData, tillValue });
  const { eBillConfig, sendWebHookData } = eBillWebhook;
  sendWebHookDataRef.current = sendWebHookData;

  // Side Menu Drawer
  const openSideMenu = () => {
    setSideMenuDrawervisible(true);
  };
  const onClose = () => {
    setSideMenuDrawervisible(false);
  };

  // --- useSalesRepresentative ---
  const salesRepOps = useSalesRepresentative({
    cart,
    setCart,
    tillData,
    selectedProductInCart,
    setOpenSalesRepRef,
  });
  const {
    openSalesRep,
    setOpenSalesRep,
    salesRepModalOpen,
    setSalesRepModalOpen,
    salesRepresent,
    setSalesRepresent,
    salesRepresentDefaultLine,
    setSalesRepresentDefaultLine,
    salesRepresentSearchInput,
    setSalesRepresentSearchInput,
    salesRepValue,
    setSalesRepValue,
    salesRepRef,
    salesReprestiveList,
    filteredSalesRepresentList,
    setFilteredSalesRepresentList,
    handleSalesRepresentive,
    handleSalesRepresentSearchInput,
  } = salesRepOps;

  // --- useZoneNavigation (keyboard nav Phase 1) ---

  // --- Keyboard shortcut handlers (Phase 3) ---
  const layoutShortcutHandlersRef = useRef({});
  const completePaymentProcessingRef = useRef(false);
  const newShortcutHandlers = {
    [ACTIONS.CUSTOMER_SEARCH]: () => setDisplayCustomerSearch(true),
    [ACTIONS.MANUAL_DISCOUNT]: () => {
      if (cart.items?.length > 0) setManualDiscountModalVisible(true);
    },
    [ACTIONS.GIFT_CARD]: () => setIsGiftCardFlag(true),
    [ACTIONS.CASH_MANAGEMENT]: () => setAddCashFlag(true),
    [ACTIONS.ITEM_NOTES]: () => {
      if (selectedProductInCart?.key != null) {
        setSelectedProductForNotes(selectedProductInCart);
        setNotesPopup(true);
      }
    },
    [ACTIONS.CUSTOMER_WALLET]: () => {
      if (cart.customer?.cwrCustomerId) setShowCustomerWallet(true);
    },
    [ACTIONS.COMPLETE_PAYMENT]: async () => {
      if (paymentModal && !completePaymentProcessingRef.current) {
        completePaymentProcessingRef.current = true;
        try {
          await processOrder();
        } finally {
          completePaymentProcessingRef.current = false;
        }
      }
    },
    [ACTIONS.QUICK_CASH]: () => {
      if (cart.items?.length > 0) {
        openPaymentModal();
        const cashMethod = tillDataPaymentMethods.find((m) => m.name?.toLowerCase() === "cash");
        if (cashMethod) {
          setTimeout(() => {
            setSelectedPaymentMethod(cashMethod);
            // BUG-2612: a round-off Cash on the supermarket layout gets the same
            // rounded amount a click on Cash gives; every other case is unchanged.
            const liveCart = useCartStore.getState().cart || cart;
            const roundOff = getCoreRoundOffTender({
              tillLayout,
              paymentMethod: cashMethod,
              remaining: Math.abs(Number(liveCart.total) || 0) - (Number(liveCart.paid) || 0),
              isReturn: liveCart.isReturn === true || Number(liveCart.total) < 0,
              isExchange: useTransactionStore.getState().selectedReturnType === "EXCHANGE",
              tillData,
              precision,
            });
            if (roundOff) {
              setAmount(roundOff.amount);
              setOverPayedAmount(roundOff.overPayedAmount);
            } else {
              setAmount(cart.total);
            }
          }, 100);
        }
      }
    },
    [ACTIONS.ADDITIONAL_CHARGES]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.PRINT_RECEIPT]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.PRODUCT_SYNC]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.SEND_KOT]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.GUEST_COUNT]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.TRANSFER_TABLE]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.MERGE_TABLES]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.FULFILLMENT_DI]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.FULFILLMENT_DE]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.FULFILLMENT_TA]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(),
    [ACTIONS.SWITCH_SECTION]: (e, shortcut) => layoutShortcutHandlersRef.current[shortcut.action]?.(e),
  };

  // --- usePOSKeyboard ---
  const posKeyboard = usePOSKeyboard({
    posConfig,
    cart,
    tillLayout,
    setSalesRepModalOpen: (v) => setSalesRepModalOpen(v),
    setSalesRepresentSearchInput: (v) => setSalesRepresentSearchInput(v),
    salesReprestiveList,
    setFilteredSalesRepresentList: (v) => setFilteredSalesRepresentList(v),
    setSelectedRowKeys,
    openPaymentModal,
    setDisplayOrderHistory,
    setDisplayParkedBillModal,
    setIsQtyUpdate,
    parkBill,
    confirmDiscardCart,
    closeProductPanel,
    setDisplayBatchSelection,
    closeCustomerSearch,
    closePaymentModal,
    increaseProductQty,
    decreaseProductQty,
    deleteProduct,
    selectedProductInCart,
    setSelectedProductInCart,
    selectedRowKeys,
    paymentModal,
    selectedItemsModalOpen,
    setSelectedPaymentMethod,
    tillDataPaymentMethods,
    handleCartTotalClick,
    zoneNavigation,
    layoutType: keyboardLayoutType,
    newShortcutHandlers,
    // Any customer overlay open (search / add / edit / UAE) must suppress cart
    // shortcuts, regardless of focus. The customer search box reuses the
    // product-search id, so a focus/id check alone cannot tell them apart.
    customerOverlayOpen:
      displayCustomerSearch ||
      displayAddNewCustomer ||
      displayEditOldCustomer ||
      displayUAECustomerSearch ||
      displayUAECustomer ||
      isCustomerDrawerOpen,
  });
  const { cartItemsLengthRef, selectedProductInCartRef, selectedRowKeysRef, paymentModalStateRef } = posKeyboard;

  // --- useDiscountOperations ---
  const discountOps = useDiscountOperations({
    tillData,
    manualDiscountModalVisible,
    manualDiscountInput,
    setManualDiscountInput,
    setManualDiscountTypes,
    setEnableManualDiscountInput,
    manualDiscountTypes,
    setSelectedManualDiscountTypeValue,
    manualDiscountForm,
    enableManualDiscountInput,
    discountVerificationData,
    discountAppliedValues: useTransactionStore.getState().discountAppliedData,
    totalBillDiscountData,
    discountApproval,
    processManualDiscount,
    discountAdditionalInfoForm,
    setDiscountAdditionalInfo,
    setDiscountVerifictionData,
  });
  const { externalCouponTypes, setSelectedExternalCouponType, setSelectedManualDiscountType, handleManualDiscountKeyPress, discountInforApplication } = discountOps;

  // Utility functions
  const posLogActivity = (record, activity) => posLogActivityUtil(record, activity, { db, cart });
  posLogActivityRef.current = posLogActivity;

  const handleManagement = () => {
    setManagementScreenShow(true);
  };
  const handleCloseModal = () => {
    history.push("/dashboard");
  };
  const validateUserPin = async (_user, pin) => {
    const configuredPin = posConfig?.lockScreenPin || tillData?.tillAccess?.cwrTill?.lockScreenPin;
    if (!configuredPin) {
      console.warn("[POS] No lockScreenPin configured in posConfig or tillData — PIN validation will always fail");
      return false;
    }
    return pin === String(configuredPin);
  };

  // Sync cartRef
  useEffect(() => {
    cartRef.current = { ...cart };
  }, [cart]);

  // ═══════════════════════════════════════════════════════════════════
  // WALLET & GIFT CARD TOPUP STATE + HANDLERS (from roadmap)
  // ═══════════════════════════════════════════════════════════════════
  const [currentCardInput, setCurrentCardInput] = useState("");
  const [currentCashInput, setCurrentCashInput] = useState("");
  const [currentUPIInput, setCurrentUPIInput] = useState("");
  const [dynamicPaymentInput, setDynamicPaymentInput] = useState("");
  const [topupAmount, setTopupAmount] = useState("");
  const [cashTendered, setCashTendered] = useState(0);
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showCardAmountInput, setShowCardAmountInput] = useState(false);
  const [cardPaymentForm] = Form.useForm();
  const [walletFlowPending, setWalletFlowPending] = useState(false);
  const [walletFlowData, setWalletFlowData] = useState(null);
  const [showGiftCardTopup, setShowGiftCardTopup] = useState(false);
  const [giftCardTopupAmount, setGiftCardTopupAmount] = useState("");
  const [giftCardTopupPaymentMethod, setGiftCardTopupPaymentMethod] = useState(null);
  const [giftCardTopupCashInput, setGiftCardTopupCashInput] = useState("");
  const [giftCardTopupCardInput, setGiftCardTopupCardInput] = useState("");
  const [giftCardTopupUPIInput, setGiftCardTopupUPIInput] = useState("");
  const [giftCardGenericInput, setGiftCardGenericInput] = useState("");
  const [giftCardNumber, setGiftCardNumber] = useState("");
  const [giftCardPin, setGiftCardPin] = useState("");
  const [giftCardTopupPaymentPage, setGiftCardTopupPaymentPage] = useState(0);
  const [giftCardTopupFocusedField, setGiftCardTopupFocusedField] = useState("topupAmount");
  const [giftCardTopupDenomKeyboard, setGiftCardTopupDenomKeyboard] = useState(false);
  const [giftCardTopupShowCardAmountInput, setGiftCardTopupShowCardAmountInput] = useState(false);
  const [giftCardTopupCardForm] = Form.useForm();
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const debounceRef = useRef(null);
  const enterRef = useRef(false);

  // Sync refs for lazy access by usePaymentFlow
  showGiftCardTopupRef.current = showGiftCardTopup;

  const currenciesList = tillData?.tillAccess?.csBunit?.currencies;

  // --- Wallet matched payments ---
  const rawWalletPayments = useTransactionStore.getState().walletPayments || [];
  const matchedPayments = rawWalletPayments.map((payment) => {
    const matchedMethod = showPayments.find((method) => method.finPaymentmethodId === payment.finPaymentmethodID);
    return {
      ...matchedMethod,
      ...payment,
      amount: Number(payment.amount).toFixed(precision),
    };
  });

  // --- Wallet top-up handler ---
  const handleWalletTopUp = async (amount) => {
    if (!cart?.customer?.cwrCustomerId) {
      message.error(`${messages.errors.customer.not_selected}`);
      return;
    }
    const paymentAmount = Number.parseFloat(amount || 0);
    const storedPayments = useTransactionStore.getState().walletPayments;
    if (!paymentAmount || isNaN(paymentAmount) || paymentAmount <= 0) {
      message.error(`${messages.info.invalid_topup}`);
      return;
    }
    // Block topup if no payments collected — without this guard, the server
    // received empty payments[] arrays and the wallet got credited without an
    // actual payment trail. (Issue #18)
    // if (!Array.isArray(storedPayments) || storedPayments.length === 0) {
    //   message.error("Please add at least one payment before completing the top-up.");
    //   return;
    // }
    // BUG-2583: a payment without an amount is never sent (the server rejects the whole top-up).
    if (hasInvalidTopupPaymentAmount(storedPayments)) {
      console.error("[BUG-2583] wallet top-up not sent: payment without an amount", storedPayments);
      message.error(TOPUP_PAYMENT_AMOUNT_MISSING);
      return;
    }
    const payload = {
      bunitId: tillData.tillAccess.csBunit.csBunitId,
      customerId: cart.customer.cwrCustomerId,
      trxType: "DP",
      trxValue: paymentAmount,
      tillId: tillData?.tillAccess.cwrTill.cwrTillID,
      payments: (storedPayments || []).map(({ count, isBankTransfer, ...p }) => ({
        finPaymentmethodID: p.finPaymentmethodID || p.finPaymentmethodId,
        amount: p.amount,
        ...(isBankTransfer === "Y" && {
          financialAccountId: p?.financialAccountId || null,
          transactionTime: p.transactionTime || null,
          imageUrl: p.imageUrl || null,
          transactionId: p?.transactionId || null,
        }),
      })),
      transactionDetails: { paymnet: paymentAmount },
    };
    try {
      const response = await upsertWalletTransactionService(payload);
      if (response?.status === "200") {
        await db?.tillEvents
          .where("tillStatus")
          .equals("open")
          .modify((event) => {
            const walletTransactions = [...(event.walletTransactions || [])];
            storedPayments.forEach((payment) => {
              const existingIndex = payment.isBankTransfer === "Y" ? -1 : walletTransactions.findIndex((wt) => wt.finPaymentmethodID === payment.finPaymentmethodID);
              if (existingIndex !== -1) {
                walletTransactions[existingIndex].amount += payment.amount;
              } else {
                walletTransactions.push({ ...payment });
              }
            });
            event.walletTransactions = walletTransactions;
          });
        message.success(response.message);
        fetchWalletData();
        setShowTopupModal(false);
        setTopupAmount("");
        setCurrentCashInput("");
        setCurrentCardInput("");
        setCurrentUPIInput("");
        setDynamicPaymentInput("");
        setCashTendered(0);
        setShowCardAmountInput(false);
        setSelectedPaymentMethod({});
        cardPaymentForm.resetFields();
        useTransactionStore.getState().setWalletPayments([]);
        localStorage.removeItem("cardPaymentDetails");
        localStorage.removeItem("walletDataForCardIntegration");
        localStorage.removeItem("giftCardIntegrationTopupData");
      } else {
        message.error(response?.message || `${messages.info.topup_failed}`);
      }
    } catch (err) {
      console.error("Failed to top up wallet:", err);
      message.error(`${messages.info.topup_record_failed}`);
    }
  };

  // --- Wallet refund handler ---
  const handleWalletRefundComplete = async (amount) => {
    if (!cart?.customer?.cwrCustomerId) {
      message.error("Customer not selected");
      return;
    }
    const paymentAmount = Number.parseFloat(amount || 0);
    const storedPayments = useTransactionStore.getState().walletPayments;
    const cleanedPayments = (storedPayments || []).map(({ count, isBankTransfer, ...p }) => ({
      finPaymentmethodID: p.finPaymentmethodID || p.finPaymentmethodId,
      amount: p.amount,
      ...(isBankTransfer === "Y" && {
        financialAccountId: p?.financialAccountId || null,
        transactionTime: p.transactionTime || null,
        imageUrl: p.imageUrl || null,
        transactionId: p?.transactionId || null,
      }),
    }));
    const payload = {
      bunitId: tillData.tillAccess.csBunit.csBunitId,
      customerId: cart.customer.cwrCustomerId,
      trxType: "WD",
      trxValue: paymentAmount,
      tillId: tillData?.tillAccess.cwrTill.cwrTillID,
      payments: cleanedPayments,
      transactionDetails: { paymnet: paymentAmount },
    };
    try {
      const response = await upsertWalletTransactionService(payload);
      if (response?.status === "200") {
        await db?.tillEvents
          .where("tillStatus")
          .equals("open")
          .modify((event) => {
            const walletTransactions = [...(event.walletTransactions || [])];
            storedPayments.forEach((payment) => {
              const existingIndex = payment.isBankTransfer === "Y" ? -1 : walletTransactions.findIndex((wt) => wt.finPaymentmethodID === payment.finPaymentmethodID);
              if (existingIndex !== -1) {
                walletTransactions[existingIndex].amount += payment.amount;
              } else {
                walletTransactions.push({ ...payment });
              }
            });
            event.walletTransactions = walletTransactions;
          });
        message.success("Wallet refunded successfully");
        await fetchWalletData();
        setShowRefundModal(false);
        setTopupAmount("");
        setCurrentCashInput("");
        setCurrentCardInput("");
        setDynamicPaymentInput("");
        setCurrentUPIInput("");
        setCashTendered(0);
        setShowCardAmountInput(false);
        setSelectedPaymentMethod({});
        cardPaymentForm.resetFields();
        useTransactionStore.getState().setWalletPayments([]);
        localStorage.removeItem("cardPaymentDetails");
        localStorage.removeItem("walletDataForCardIntegration");
        localStorage.removeItem("giftCardIntegrationTopupData");
        setIsProcessingTopup(false);
      } else {
        message.error(response?.message || "Refund failed");
        setIsProcessingTopup(false);
      }
    } catch (err) {
      console.error("[WalletRefund] Refund error:", err);
      message.error("Refund error");
      setIsProcessingTopup(false);
    }
  };

  // --- Auto-fill enter amount for wallet ---
  const autoFillEnterAmount = (amount, paymentMethod = selectedPaymentMethod) => {
    if (!paymentMethod) return;
    if (amount === "" || isNaN(amount)) return;
    const totalPaid = matchedPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const remaining = parseFloat(amount) - totalPaid;
    if (remaining <= 0) return;
    const value = remaining.toString();
    if (paymentMethod.name === "Cash") setCurrentCashInput(value);
    else if (paymentMethod.name === "Card") setCurrentCardInput(value);
    else if (paymentMethod.name === "UPI") setCurrentUPIInput(value);
    else setDynamicPaymentInput(value);
  };

  // --- Wallet post-payment handler ---
  const handleWalletPostPayment = async (walletData) => {
    const walletDataForCard = JSON.parse(localStorage.getItem("walletDataForCardIntegration"));
    const sourceData = walletDataForCard || walletData || walletFlowData;
    if (!sourceData) return;
    const {
      paymentAmount,
      topupTotal,
      totalPayments,
      newCashTendered,
      selectedPaymentMethod: selPM,
      selectedCustomer: selCust,
      customersData: custData,
      showRefundModal: showRefund,
      currenciesList: currList,
      topupAmount: topAmt,
    } = sourceData;
    const latestTotalPayments = matchedPayments.reduce((sum, payment) => sum + Number.parseFloat(payment.amount || 0), 0);
    const updatedTotalPayments = (Number.parseFloat(totalPayments) + Number.parseFloat(paymentAmount)).toFixed(precision);
    const newTransaction = {
      id: (selCust?.transactions?.length || 0) + 1,
      date: new Date().toISOString().split("T")[0],
      time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
      type: "Credit",
      amount: Number.parseFloat(paymentAmount),
      description: `Wallet Top-up via ${selPM.name}`,
      reference: `TXN${String(Date.now()).slice(-3)}`,
      balance: (selCust?.currentBalance || 0) + Number.parseFloat(paymentAmount),
    };
    const updatedCustomer = {
      ...selCust,
      currentBalance: (selCust?.currentBalance || 0) + Number.parseFloat(paymentAmount),
      lastTransaction: new Date().toISOString().split("T")[0],
      transactions: [newTransaction, ...(selCust?.transactions || [])],
    };
    if (typeof setCustomersData === "function") {
      if (Array.isArray(custData)) {
        const updatedCustomers = custData.map((customer) => (customer?.id === selCust?.id ? updatedCustomer : customer));
        setCustomersData(updatedCustomers);
      } else {
        setCustomersData(updatedCustomer);
      }
    }
    setSelectedCustomer(updatedCustomer);
    setCurrentCashInput("");
    setCurrentCardInput("");
    setCurrentUPIInput("");
    setCashTendered(newCashTendered);
    const remainingAmount = Number.parseFloat(topupTotal) - Number.parseFloat(updatedTotalPayments);
    if (remainingAmount <= 0) {
      setCurrentCashInput("");
      setCurrentCardInput("");
      setCurrentUPIInput("");
      setDynamicPaymentInput("");
    }
    if (Number.parseFloat(updatedTotalPayments) >= Number.parseFloat(topupTotal)) {
      if (showRefund) {
        const refundAmount = Number.parseFloat(topAmt || 0);
        const updatedTotal = Number.parseFloat(updatedTotalPayments);
        const customerBalance = selCust?.[0]?.currentBalance || 0;
        if (refundAmount > customerBalance) {
          message.error("Refund amount cannot exceed wallet balance");
          setIsProcessingTopup(false);
          return;
        }
        if (updatedTotal > refundAmount) {
          message.error("Payment exceeds refund amount");
          setIsProcessingTopup(false);
          return;
        }
        if (updatedTotal === refundAmount) {
          await handleWalletRefundComplete(refundAmount);
        } else {
          message.success(`${selPM.name} payment of ${paymentAmount} added. Remaining: ${(refundAmount - updatedTotal).toFixed(precision)}`);
          setIsProcessingTopup(false);
        }
        return;
      } else {
        await handleWalletTopUp(topAmt);
      }
    } else {
      const cList = currList || currenciesList;
      message.success(
        `${selPM.name} payment of ${cList?.[0]?.symbolRightSide === "N" ? cList[0].currSymbol : ""}${paymentAmount}${
          cList?.[0]?.symbolRightSide === "Y" ? cList[0].currSymbol : ""
        } added. Remaining: ${cList?.[0]?.symbolRightSide === "N" ? cList[0].currSymbol : ""}${(topupTotal - updatedTotalPayments).toFixed(precision)}${
          cList?.[0]?.symbolRightSide === "Y" ? cList[0].currSymbol : ""
        }`
      );
      localStorage.removeItem("walletDataForCardIntegration");
      setIsProcessingTopup(false);
    }
  };
  handleWalletPostPaymentRef.current = handleWalletPostPayment;

  // --- Wallet enter total qty handler ---
  const enterTotalQtyForWallet = async () => {
    if (!selectedPaymentMethod || !selectedPaymentMethod.name) {
      message.warning("Please select a payment method to complete the payment");
      return;
    }
    if (typeof setCart !== "function") {
      console.error("setCart is not a function. Cannot update cart.");
      message.error(`${messages.errors.payments.configuration_payment_failed}`);
      return;
    }
    const currentInput =
      selectedPaymentMethod.name === "Cash"
        ? currentCashInput
        : selectedPaymentMethod.name === "Card"
          ? currentCardInput
          : selectedPaymentMethod.name === "UPI"
            ? currentUPIInput
            : dynamicPaymentInput;
    if (!currentInput || Number.parseFloat(currentInput) <= 0) {
      message.error(`${messages.info.valid_amount}`);
      return;
    }
    if (!topupAmount || Number.parseFloat(topupAmount) <= 0) {
      message.error(`${messages.info.valid_topup_amount}`);
      return;
    }
    const paymentAmount = Number.parseFloat(currentInput).toFixed(precision);
    const topupTotal = Number.parseFloat(topupAmount).toFixed(precision);
    const newCashTendered = (Number.parseFloat(cashTendered) || 0) + Number.parseFloat(paymentAmount);
    const totalPayments = matchedPayments.reduce((sum, payment) => sum + Number.parseFloat(payment.amount || 0), 0).toFixed(precision);
    if (Number.parseFloat(totalPayments) + Number.parseFloat(paymentAmount) > topupTotal) {
      message.error(`${selectedPaymentMethod.name} ${messages.warnings.payment.payment_multiple_check_topup} ${topupTotal - totalPayments}`);
      return;
    }
    setIsProcessingTopup(true);
    try {
      if (selectedPaymentMethod.name === "Cash") {
        const filteredCurrencies = tillData?.tillAccess?.csBunit?.currencies[0]?.denominations?.sort((a, b) => a.value - b.value) || [];
        if (filteredCurrencies.length > 0) {
          const result = modulo(Number.parseFloat(currentInput || 0), filteredCurrencies[0]?.value || 1);
          if (result !== 0) {
            message.warning(`${messages.warnings.payment.payment_multiple_check} ${filteredCurrencies[0]?.value}`);
            setIsProcessingTopup(false);
            return;
          }
        }
      }
      if (selectedPaymentMethod?.name?.toLowerCase() === "card" && selectedPaymentMethod?.integratedPayment && posConfig?.show_card_payment_popup === "Y") {
        if (isValidAmount(paymentAmount)) {
          setShowCardPaymnteModal(true);
        } else {
          message.warning("Kindly enter the amount before proceeding");
          setIsProcessingTopup(false);
          return;
        }
      } else if (selectedPaymentMethod?.name?.toLowerCase() === "card" && !selectedPaymentMethod?.integratedPayment && posConfig?.show_card_payment_popup === "Y") {
        if (isValidAmount(paymentAmount)) {
          coreState.setPaymentType?.("manual");
          setCardPaymnetError(true);
          setShowCardPaymnteModal(true);
          const walletDataForCardIntegration = {
            paymentAmount,
            topupTotal,
            totalPayments,
            newCashTendered,
            selectedPaymentMethod,
            selectedCustomer,
            customersData,
            showRefundModal,
            currenciesList,
            topupAmount,
          };
          localStorage.setItem("walletDataForCardIntegration", JSON.stringify(walletDataForCardIntegration));
          return;
        } else {
          message.warning("Kindly enter the amount before proceeding");
          setIsProcessingTopup(false);
          return;
        }
      }
      if (
        (selectedPaymentMethod?.name?.toLowerCase() === "card" && selectedPaymentMethod?.integratedPayment) ||
        (selectedPaymentMethod?.name?.toLowerCase() === "upi" && selectedPaymentMethod?.integratedPayment)
      ) {
        setWalletFlowPending(true);
        setWalletFlowData({
          paymentAmount,
          topupTotal,
          totalPayments,
          newCashTendered,
          selectedPaymentMethod,
          selectedCustomer,
          customersData,
          showRefundModal,
          currenciesList,
          topupAmount,
        });
      } else {
        setWalletFlowData({
          paymentAmount,
          topupTotal,
          totalPayments,
          newCashTendered,
          selectedPaymentMethod,
          selectedCustomer,
          customersData,
          showRefundModal,
          currenciesList,
          topupAmount,
        });
      }
      const walletDataForCardIntegration = {
        paymentAmount,
        topupTotal,
        totalPayments,
        newCashTendered,
        selectedPaymentMethod,
        selectedCustomer,
        customersData,
        showRefundModal,
        currenciesList,
        topupAmount,
      };
      localStorage.setItem("walletDataForCardIntegration", JSON.stringify(walletDataForCardIntegration));
      await requestPayment(selectedPaymentMethod, paymentAmount, undefined, true, walletDataForCardIntegration);
      if (selectedPaymentMethod?.name?.toLowerCase() === "card" || selectedPaymentMethod?.name?.toLowerCase() === "upi") {
        return;
      }
      handleWalletPostPayment({
        paymentAmount,
        topupTotal,
        totalPayments,
        newCashTendered,
        selectedPaymentMethod,
        selectedCustomer,
        customersData,
        showRefundModal,
        currenciesList,
        topupAmount,
      });
    } catch (error) {
      console.error(`${selectedPaymentMethod.name} top-up error:`, error);
      message.error(`${selectedPaymentMethod?.name || "Payment"} ${messages.errors.payments.wallet_topup_failed}`);
      setIsProcessingTopup(false);
    }
  };

  // --- Gift Card Topup ---
  const rawGiftCardPayments = useTransactionStore.getState().walletPayments || [];
  const resetGiftCardTopup = () => {
    setShowGiftCardTopup(false);
    setGiftCardTopupAmount("");
    setGiftCardTopupCashInput("");
    setGiftCardTopupCardInput("");
    setGiftCardTopupUPIInput("");
    setGiftCardGenericInput("");
    setGiftCardTopupPaymentPage(0);
    setGiftCardTopupPaymentMethod(null);
    setGiftCardTopupFocusedField("topupAmount");
    setGiftCardTopupDenomKeyboard(false);
    setGiftCardTopupShowCardAmountInput(false);
    giftCardTopupCardForm.resetFields();
    useTransactionStore.getState().setWalletPayments([]);
    localStorage.removeItem("cardPaymentDetails");
    localStorage.removeItem("walletDataForCardIntegration");
    localStorage.removeItem("giftCardIntegrationTopupData");
  };

  const handleCheckGiftCardBalance = async () => {
    if (!giftCardNumber) {
      message.warning("Please enter Gift Card Number");
      return;
    }
    setIsCheckingBalance(true);
    try {
      const data = {
        query: `query {
          checkGiftCardBalance(cardNo: "${giftCardNumber}", cardPin: ${giftCardPin ? `"${giftCardPin}"` : null}){
              currentBalance
              cardGroup
              status
              message
            }
        }`,
      };
      const giftCardResponse = await checkGiftCard(data);
      if (giftCardResponse.data.data.checkGiftCardBalance.length > 0 && giftCardResponse.data.data.checkGiftCardBalance[0].status === "200") {
        const balance = giftCardResponse.data.data.checkGiftCardBalance[0].currentBalance;
        setGiftCardBalance(balance ?? 0);
        message.success("Gift Card balance retrieved successfully");
      } else {
        message.error(`${giftCardResponse.data.data.checkGiftCardBalance[0].message}`);
        setGiftCardBalance(null);
      }
    } catch (error) {
      message.error("Failed to check Gift Card balance");
      setGiftCardBalance(null);
    } finally {
      setIsCheckingBalance(false);
    }
  };

  const giftCardMatchedPayments = rawGiftCardPayments.map((payment) => {
    const matchedMethod = showPayments?.find((method) => method.finPaymentmethodId === payment.finPaymentmethodID);
    return { ...matchedMethod, ...payment, amount: Number(payment.amount).toFixed(precision) };
  });

  const currSymbol = currenciesList?.[0]?.symbolRightSide === "N" ? currenciesList[0].currSymbol : "";
  const currSymbolRight = currenciesList?.[0]?.symbolRightSide === "Y" ? currenciesList[0].currSymbol : "";

  const currentPaymentInput =
    giftCardTopupPaymentMethod?.name?.toLowerCase() === "cash"
      ? giftCardTopupCashInput
      : giftCardTopupPaymentMethod?.name?.toLowerCase() === "card"
        ? giftCardTopupCardInput
        : giftCardTopupPaymentMethod?.name?.toLowerCase() === "upi"
          ? giftCardTopupUPIInput
          : giftCardGenericInput;

  const giftCardTotalPaid = giftCardMatchedPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const giftCardAutoFillAmount = (topupAmt, paymentMethod = giftCardTopupPaymentMethod) => {
    if (!paymentMethod || !topupAmt || isNaN(topupAmt)) return;
    const rawPayments = useTransactionStore.getState().walletPayments || [];
    const totalPaid = rawPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const remaining = parseFloat(topupAmt) - totalPaid;
    if (remaining <= 0) return;
    const value = remaining.toString();
    const name = paymentMethod.name?.toLowerCase();
    if (name === "cash") setGiftCardTopupCashInput(value);
    else if (name === "card") setGiftCardTopupCardInput(value);
    else if (name === "upi") setGiftCardTopupUPIInput(value);
    else setGiftCardGenericInput(value);
  };

  const finalizeGiftCardTopup = async (topupTotal) => {
    const cs = currenciesList?.[0]?.symbolRightSide === "N" ? currenciesList[0].currSymbol : "";
    const csr = currenciesList?.[0]?.symbolRightSide === "Y" ? currenciesList[0].currSymbol : "";
    try {
      const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
      const latestPayments = useTransactionStore.getState().walletPayments || [];
      // BUG-2583: same rule for a gift card top-up. The payments stay so the cashier can fix them.
      if (hasInvalidTopupPaymentAmount(latestPayments)) {
        console.error("[BUG-2583] gift card top-up not sent: payment without an amount", latestPayments);
        message.error(TOPUP_PAYMENT_AMOUNT_MISSING);
        return;
      }
      const latestMatchedPayments = latestPayments.map((payment) => {
        const matchedMethod = showPayments?.find((m) => m.finPaymentmethodId === payment.finPaymentmethodID);
        return { ...matchedMethod, ...payment };
      });
      const paymentsGQL = latestMatchedPayments.map((p) => `{ finPaymentmethodID: "${p.finPaymentmethodId || p.finPaymentmethodID}" amount: ${parseFloat(p.amount)} }`).join(",");
      const upsertData = {
        query: `
        mutation {
          upsertGiftCardTransaction(giftCardTransactions: [{
            cardNo: ${giftCardNumber ? `"${giftCardNumber}"` : null}
            type: "TO"
            referenceNo: "${uuidv4().replace(/-/g, "").toUpperCase()}"
            amount: ${parseFloat(topupTotal)}
            cardPin: ${giftCardPin ? `"${giftCardPin}"` : null}
            tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
            b2c_Customer_Id: ${cart.customer.cwrCustomerId ? `"${cart.customer.cwrCustomerId}"` : null}
            bunitId: "${bunitId}"
            payments: [${paymentsGQL}]
          }]) {
            status
            message
          }
        }`,
      };
      const topupResponse = await upsertGiftCardTransaction(upsertData);
      const result = topupResponse?.data?.data?.upsertGiftCardTransaction;
      if (result?.status === "200") {
        message.success(`Gift Card topped up with ${cs}${topupTotal}${csr}`);
        useTransactionStore.getState().setWalletPayments([]);
        resetGiftCardTopup();
        await handleCheckGiftCardBalance();
      } else {
        message.error(result?.message || "Gift Card top-up failed");
        useTransactionStore.getState().setWalletPayments([]);
        resetGiftCardTopup();
      }
    } catch (topupErr) {
      message.error("Gift Card top-up service failed");
      useTransactionStore.getState().setWalletPayments([]);
      resetGiftCardTopup();
    }
  };

  const handleGiftCardTopupPostPayment = async (giftCardData) => {
    const gitCardDataForCardIntegration = JSON.parse(localStorage.getItem("giftCardIntegrationTopupData"));
    const { paymentValue, topupTotal } = giftCardData || gitCardDataForCardIntegration;
    const updatedTotalPaid = (giftCardTotalPaid + paymentValue).toFixed(precision);
    setGiftCardTopupCashInput("");
    setGiftCardTopupCardInput("");
    setGiftCardTopupUPIInput("");
    setGiftCardGenericInput("");
    giftCardAutoFillAmount(giftCardTopupAmount);
    if (parseFloat(updatedTotalPaid) >= parseFloat(topupTotal)) {
      await finalizeGiftCardTopup(topupTotal);
    } else {
      const remaining = (parseFloat(topupTotal) - parseFloat(updatedTotalPaid)).toFixed(precision);
      message.success(
        `${giftCardTopupPaymentMethod.name} payment of ${currSymbol}${paymentValue.toFixed(precision)}${currSymbolRight} added. Remaining: ${currSymbol}${remaining}${currSymbolRight}`
      );
      localStorage.removeItem("giftCardIntegrationTopupData");
    }
  };
  handleGiftCardTopupPostPaymentRef.current = handleGiftCardTopupPostPayment;

  const handleEnterTopup = async () => {
    const topupValue = parseFloat(giftCardTopupAmount);
    const paymentValue = parseFloat(currentPaymentInput);
    if (!topupValue || topupValue <= 0) {
      message.warning("Please enter a valid top-up amount");
      return;
    }
    if (!giftCardTopupPaymentMethod) {
      message.warning("Please select a payment method");
      return;
    }
    if (!paymentValue || paymentValue <= 0) {
      message.warning("Please enter a valid payment amount");
      return;
    }
    const topupTotal = parseFloat(topupValue).toFixed(precision);
    if (giftCardTotalPaid + paymentValue > parseFloat(topupTotal)) {
      const remaining = (parseFloat(topupTotal) - giftCardTotalPaid).toFixed(precision);
      message.error(`${giftCardTopupPaymentMethod.name} payment exceeds top-up amount. Remaining: ${currSymbol}${remaining}${currSymbolRight}`);
      return;
    }
    try {
      await requestPayment(giftCardTopupPaymentMethod, paymentValue.toFixed(precision), undefined, true);
      if (
        (giftCardTopupPaymentMethod?.name?.toLowerCase() === "card" && giftCardTopupPaymentMethod?.integratedPayment) ||
        (giftCardTopupPaymentMethod?.name?.toLowerCase() === "upi" && giftCardTopupPaymentMethod?.integratedPayment)
      ) {
        const giftCardTopupData = { paymentValue, topupTotal, paymentMethod: giftCardTopupPaymentMethod?.name, timestamp: Date.now() };
        localStorage.setItem("giftCardIntegrationTopupData", JSON.stringify(giftCardTopupData));
        return;
      }
      handleGiftCardTopupPostPayment({ paymentValue, topupTotal });
    } catch (err) {
      message.error("Gift Card top-up failed");
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // BUILD COMPONENT PROPS — spread all hook results + local overrides
  // ═══════════════════════════════════════════════════════════════════
  const componentProps = buildComponentProps({
    // Spread hook return objects (order matters — later spreads override earlier)
    ...cashManagement,
    ...cartOperations,
    ...paymentFlow,
    ...giftCard,
    ...orderHistory,
    ...parkedBills,
    ...salesReturn,
    ...customerOps,
    ...couponProcessing,
    ...productAddons,
    ...orderProcessing,
    ...productOps,
    ...omsOps,
    ...walletAndBatches,
    ...cartHandlers,
    ...keyboardHandlers,
    ...salesRepOps,
    ...discountOps,
    // Core state values
    ...coreState,
    // Values that need explicit listing (local vars, aliased names, overrides)
    cart,
    setCart,
    tillData,
    posConfig,
    tillDataPaymentMethods,
    retailCustomerOperationalSettings,
    retailCustomerb2bSettings,
    retailCustomerApiDataSources,
    retailCustomerGroups,
    retailCustomerRules,
    retailCustomerTabs,
    retailCustomerFields,
    customerConfig,
    customerConfigData,
    groupedFields,
    documentTypes,
    isPrintModeXML,
    isExchange,
    layoutShortcutHandlersRef,
    isRevenueCategoryFlag,
    setIsRevenueCategoryFlag,
    selectedRevenueCategoryName,
    setSelectedRevenueCategoryName,
    revenueCategoryList,
    handleSelectedRevenueCategory,
    openRevenueCategoryDrawer,
    displayClock,
    setDisplayClock,
    selectProduct,
    selectSalseProduct,
    changeOrderType,
    openSideMenu,
    onClose,
    handleManagement,
    managementScreenShow,
    setManagementScreenShow,
    posLogActivity,
    upsertPOSLog,
    setDefaultImage,
    history,
    content,
    getDisplayDate,
    loading,
    setLoading,
    // detailsLoading,
    // setDetailsLoading,
    firstInputValueFromCart,
    setFirstInputValueFromCart,
    realPriceFromCart,
    setRealPriceFromCart,
    // Wallet & Gift Card Topup props
    giftCardTopupPaymentPage,
    setGiftCardTopupPaymentPage,
    giftCardTopupFocusedField,
    setGiftCardTopupFocusedField,
    giftCardTopupDenomKeyboard,
    setGiftCardTopupDenomKeyboard,
    giftCardTopupShowCardAmountInput,
    setGiftCardTopupShowCardAmountInput,
    giftCardTopupCardForm,
    isCheckingBalance,
    setIsCheckingBalance,
    rawGiftCardPayments,
    giftCardMatchedPayments,
    giftCardNumber,
    setGiftCardNumber,
    giftCardPin,
    setGiftCardPin,
    resetGiftCardTopup,
    handleCheckGiftCardBalance,
    giftCardTopupAmount,
    setGiftCardTopupAmount,
    giftCardTopupPaymentMethod,
    setGiftCardTopupPaymentMethod,
    giftCardTopupCashInput,
    setGiftCardTopupCashInput,
    giftCardTopupCardInput,
    setGiftCardTopupCardInput,
    giftCardTopupUPIInput,
    setGiftCardTopupUPIInput,
    giftCardGenericInput,
    setGiftCardGenericInput,
    currentPaymentInput,
    giftCardTotalPaid,
    currSymbol,
    currSymbolRight,
    giftCardAutoFillAmount,
    finalizeGiftCardTopup,
    handleEnterTopup,
    showGiftCardTopup,
    setShowGiftCardTopup,
    cardPaymentForm,
    showCardAmountInput,
    setShowCardAmountInput,
    showTopupModal,
    setShowTopupModal,
    currenciesList,
    handleWalletTopUp,
    handleWalletRefundComplete,
    showRefundModal,
    setShowRefundModal,
    autoFillEnterAmount,
    isProcessingTopup,
    setIsProcessingTopup,
    matchedPayments,
    cashTendered,
    setCashTendered,
    topupAmount,
    setTopupAmount,
    dynamicPaymentInput,
    setDynamicPaymentInput,
    currentUPIInput,
    setCurrentUPIInput,
    currentCashInput,
    setCurrentCashInput,
    enterTotalQtyForWallet,
    currentCardInput,
    setCurrentCardInput,
    handleWalletPostPayment,
    handleGiftCardTopupPostPayment,
    debounceRef,
    enterRef,
    notesValue,
    setNotesValue,
    exchangeNotesValue,
    setExchangeNotesValue,
    activeCartTable,
    setActiveCartTable,
    isNewGiftCardFlag,
    setIsNewGiftCardFlag,
  });

  return (
    <div>
      <FeatureErrorBoundary name="POS Layout">
        <RenderComponent {...componentProps} />
      </FeatureErrorBoundary>
      <KeyboardShortcutsOverlay visible={zoneNavigation.helpOverlayVisible} onClose={() => zoneNavigation.setHelpOverlayVisible(false)} layout={keyboardLayoutType} />
      <FeatureErrorBoundary name="Modals">
        <CoreModals {...componentProps} />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="Returns">
        <ReturnBill {...componentProps} />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="Payment">
        <PointOfSuperMarketModals {...componentProps} />
      </FeatureErrorBoundary>

      {/* Print Template Selection Modal */}
      <PrintTemplateModal
        open={showPrintTemplateModal}
        onConfirm={handlePrintTemplateConfirm}
        onCancel={handlePrintTemplateCancel}
        templates={printTemplates}
        selectedTemplate={selectedPrintTemplate}
        setSelectedTemplate={setSelectedPrintTemplate}
      />

      <AddonRepeatModal
        open={showAddonsCustomizePopup}
        repeatDisabled={addonRepeatDisabled}
        onRepeat={() => {
          if (addonRepeatDisabled || !addonSourceItem) return;
          // TSK-3303: repeating an already-fired addon line calls addProductToCart
          // directly (bypasses addProduct's own gate), so it needs its own check.
          if (isPrepareBillModificationBlocked(cart, posConfig)) {
            message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
            return;
          }
          // BUG-2355: same bypass -- a retrieved Credit Sale is read-only unless the till allows it.
          if (isCreditSaleModificationBlocked(cart, posConfig)) {
            message.info(CREDIT_SALE_MODIFICATION_BLOCKED_MESSAGE);
            return;
          }
          // BUG-2020: same bypass as above -- gate on the till's "Allow Pre Order Update"
          // flag, matching useCartHandlers.js's addProduct. docType==="Advance" covers the
          // "Retrieve Order" path (ManagePreorder.js), which doesn't stamp preOrder -- this
          // is the same pre-existing signal cartIdentityGuard.js/usePaymentFlow.js already
          // treat as equivalent to an advance order. BUG-2020 is scoped to GPR's /preorder
          // retrieval only, not every client -- SMPurchaseReturnView.js's own Sales History
          // retrieve (used by Chocolala on /pos directly) sets the SAME docType==="Advance"
          // for an unrelated flow, so the docType signal must require the /preorder route
          // too (live-observed blocking a Chocolala cart on /pos otherwise).
          if (
            (cart?.preOrder === "Y" || (cart?.docType === "Advance" && window.location.pathname.includes("/preorder"))) &&
            posConfig?.allowPreOrderUpdate !== "Y"
          ) {
            message.info("This is a pre-order with advance payment. Cart items cannot be modified at this stage. Please proceed to complete the transaction.");
            return;
          }
          addProductToCart(
            addonSourceItem,
            1,
            false,
            cart,
            setCart,
            setSelectedProductInCart,
            deleteCart,
            processTotalManualDiscount,
            setLoader,
            salesRepresentDefaultLine,
            tillData,
            cartRef,
            productsCopy,
            salesRepresent,
            orderType,
            processBillDiscounts,
            undefined,
            setSelectedRowKeys,
            setOrderTimeDetails,
            setDisplayAddOnSelection,
            displayAddOnSelection,
            handleAddOnModal,
            manualDiscountTypes,
            CheckoutFlatDiscount,
            CheckoutPercentageDiscount,
            CheckoutTotalManualDiscount,
            setProductStock,
            selectedProductInCart,
            setTableCards,
            dispatchItemSelection,
            searchInputRef,
            false,
            setShowCompleteSaleStock,
            setCompleteSaleStockData,
            TotalBillDiscount,
            tillLayout,
            false,
            setManualDiscountInput,
            setSelectedManualDiscountTypeValue,
            removeAllDiscounts,
            setSelectedPaymentMethod,
            selectedReturnType,
            false,
            setProductBOMStock,
            setProductBOMStockData
          );
          setShowAddonsCustomizePopUp(false);
          setAddonSourceItem(null);
        }}
        onCustomize={() => {
          setAddonRepeatDisabled(false);
          setShowAddonsCustomizePopUp(false);
          setTimeout(() => {
            setDisplayAddOnSelection(true);
          }, 10);
        }}
        onClose={() => {
          setAddonRepeatDisabled(false);
          setShowAddonsCustomizePopUp(false);
        }}
      />

      <StockWarningModal visible={productStock} notFoundImg={NotFoundImg} />

      {/* BUG-2405: Complete Sale / Advance order stock check uses the common BOM drawer so a
          BOM product's component stock is shown alongside the product's own availability. */}
      <BOMStockDrawer
        open={showCompleteSaleStock && completeSaleStockData?.length > 0}
        onClose={() => {
          setShowCompleteSaleStock(false);
          setCompleteSaleStockData([]);
          localStorage.removeItem("productSegment");
        }}
        stockData={completeSaleStockData}
        precision={precision}
        notFoundImg={NotFoundImg}
      />

      <BOMStockDrawer
        open={productBOMStock && productBOMStockData?.length > 0}
        onClose={() => {
          setProductBOMStock(false);
          setProductBOMStockData([]);
        }}
        stockData={productBOMStockData}
        precision={precision}
        notFoundImg={NotFoundImg}
      />

      <NotesDrawer
        open={notesPopup}
        onClose={() => {
          setNotesPopup(false);
          setIsInputFocused(false);
        }}
        onSave={onAddNotes}
        notesValue={notesValue}
        exchangeNotesValue={exchangeNotesValue}
        setExchangeNotesValue={setExchangeNotesValue}
        activeCartTable={activeCartTable}
        setActiveCartTable={setActiveCartTable}
        onChangeInputKeyboard={onChangeInputKeyboard}
        handleKeyDown={handleKeyDown}
        notesInputRef={notesInputRef}
        selectedProductForNotes={selectedProductForNotes}
        closeIcon={Close}
        keyboardRef={keyboardRef}
        layoutName={layoutName}
        setLayoutName={setLayoutName}
        onChange={onChange}
        handleShift={handleShift}
      />

      <RejectQuotationDrawer
        rejectQuotation={rejectQuotation}
        setRejectQuotation={setRejectQuotation}
        onChange={onChange}
        keyboardRef={keyboardRef}
        handleShift={handleShift}
        layoutName={layoutName}
        setLayoutName={setLayoutName}
        onAddNotes={onAddNotes}
        loader={loader}
        modalEditState={modalEditState}
        returnProcessRef={returnProcessRef}
        setShowReturnPurchase={setShowReturnPurchase}
      />
      <CustomerDisplayBridge />

      {/* B2B IRN Generation Status Modal */}
      <Modal open={showIRNModal} closable={false} footer={null} centered width={480} zIndex={1100} maskClosable={false}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0" }}>
          <div style={{ position: "relative", marginBottom: "16px" }}>
            <div style={{ width: "80px", height: "90px", backgroundColor: "#FFF9C4", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div style={{ width: "40px", height: "4px", backgroundColor: "#4CAF50", borderRadius: "2px" }} />
                <div style={{ width: "30px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }} />
                <div style={{ width: "35px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }} />
                <div style={{ width: "25px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }} />
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                bottom: "-5px",
                right: "-5px",
                width: "28px",
                height: "28px",
                backgroundColor: "#4CAF50",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>
            </div>
          </div>

          {completedOrderId && <p style={{ margin: "8px 0", fontSize: "14px", fontWeight: "400", color: "#666" }}>{completedOrderId}</p>}
          <p style={{ margin: "8px 0", fontSize: "20px", fontWeight: "600", color: "#0F0718" }}>
            {irnStatus === "SUCCESS"
              ? "IRN Generated Successfully"
              : irnStatus === "FAILED"
              ? "IRN Generation Failed"
              : irnStatus === "INCOMPLETE"
              ? "Unable to Confirm IRN Status"
              : "Processing B2B Invoice..."}
          </p>

          <div style={{ width: "100%", margin: "16px 0", padding: "0 16px" }}>
            {[
              { label: "Invoice Processing", status: invoiceProcessing },
              { label: "Invoice Printed", status: invoicePrinted },
              { label: "IRN Generating", status: irnGenerating },
              { label: "IRN Download Ready", status: irnDownloadReady },
            ].map((step) => (
              <div key={step.label} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 0" }}>
                {step.status === "SUCCESS" ? (
                  <CheckCircleFilled style={{ fontSize: "20px", color: "#4CAF50" }} />
                ) : step.status === "FAILED" || step.status === "INCOMPLETE" ? (
                  <CloseCircleFilled style={{ fontSize: "20px", color: "#f44336" }} />
                ) : step.status === "LOADING" ? (
                  <LoadingOutlined style={{ fontSize: "20px", color: "#1890ff" }} spin />
                ) : (
                  <div style={{ width: "20px", height: "20px", borderRadius: "50%", border: "2px solid #e0e0e0" }} />
                )}
                <span style={{ fontSize: "14px", color: step.status === "PENDING" ? "#999" : "#333" }}>{step.label}</span>
              </div>
            ))}
          </div>

          {irnFailureMessage && <p style={{ margin: "8px 16px", fontSize: "13px", color: "#f44336", textAlign: "center" }}>{irnFailureMessage}</p>}

          {irnPdfUrl && (
            <Button type="link" onClick={() => window.open(irnPdfUrl, "_blank")} style={{ marginTop: "8px" }}>
              Download IRN PDF
            </Button>
          )}

          <div style={{ display: "flex", gap: "12px", marginTop: "16px", justifyContent: "center" }}>
            {/* Backend only ever gives a terminal verdict via eInvoiceStatus "IG" (generated) or
                "IF" (failed) — anything else after retries exhausted ("EInvoice Not Generated",
                "EInvoice Not Found for Order", stuck "PEN", or a fetch error) means the backend
                never actually resolved it, so that's the only state worth retrying. Once it's a
                genuine SUCCESS or FAILED verdict, retrying changes nothing — only OK applies.
                fetchIRNDetails is a read-only status poll (does not re-trigger generation), so
                re-invoking it is safe; the button disappears the moment state flips to LOADING,
                preventing a second concurrent poll. */}
            {irnGenerating === "INCOMPLETE" && pendingIRNOrderId ? (
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setIrnStatus("PENDING");
                  setIrnGenerating("LOADING");
                  setIrnDownloadReady("PENDING");
                  setIrnFailureMessage("");
                  setIrnLoading(true);
                  fetchIRNDetails(pendingIRNOrderId).then(() => setIrnLoading(false));
                }}
                type="primary"
                style={{ minWidth: "120px", height: "40px", borderRadius: "4px", backgroundColor: "#4285F4", borderColor: "#4285F4" }}
              >
                Refresh
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  setShowIRNModal(false);
                  setIrnStatus("PENDING");
                  setIrnNumber("");
                  setIrnPdfUrl("");
                  setIrnLoading(false);
                  setCompletedOrderId(null);
                  setInvoiceProcessing("PENDING");
                  setInvoicePrinted("PENDING");
                  setIrnGenerating("PENDING");
                  setIrnDownloadReady("PENDING");
                  setPendingIRNOrderId(null);
                  setIrnFailureMessage("");
                }}
                style={{ minWidth: "120px", height: "40px", borderRadius: "4px", backgroundColor: "#4285F4", borderColor: "#4285F4" }}
                disabled={irnGenerating === "LOADING"}
              >
                {irnGenerating === "LOADING" ? "Please wait..." : "OK"}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
// PointOfSale Component End

// Wrapper — blocks POS render until posConfig is available.
// Auto-retries 3 times with backoff → then shows error with manual retry + logout.
const MAX_AUTO_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 8000]; // exponential backoff

const PointOfsaleCoreGuard = (props) => {
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const tillData = useAuthStore((s) => s.tillData);
  const [autoRetryCount, setAutoRetryCount] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading | retrying | failed
  const retryTimerRef = useRef(null);
  const guardMountRef = useRef(null);
  if (guardMountRef.current === null) {
    guardMountRef.current = performance.now();
    trace("BootDiag", "Guard mount", { hasPosConfig: !!posConfig, hasTillData: !!tillData?.tillAccess });
  }

  // Auto-retry with exponential backoff
  useEffect(() => {
    if (posConfig && tillData) {
      if (phase !== "loading") {
        trace("BootDiag", "Guard unblocked", {
          elapsedMs: Math.round(performance.now() - guardMountRef.current),
          autoRetryCount,
        });
      }
      setPhase("loading");
      setAutoRetryCount(0);
      return;
    }
    if (autoRetryCount >= MAX_AUTO_RETRIES) {
      trace("BootDiag", "Guard failed (max retries)", { elapsedMs: Math.round(performance.now() - guardMountRef.current) });
      setPhase("failed");
      return;
    }

    setPhase("retrying");
    const delay = RETRY_DELAYS[autoRetryCount] || 8000;
    trace("BootDiag", "Guard retry scheduled", {
      autoRetryCount,
      delay,
      elapsedMs: Math.round(performance.now() - guardMountRef.current),
      missing: !posConfig ? "posConfig" : "tillData",
    });
    retryTimerRef.current = setTimeout(async () => {
      try {
        await usePOSConfigStore.persist.rehydrate();
        await useAuthStore.getState().rehydrateFromStorage?.();
      } catch (err) {
        console.error("[POS] Store rehydration retry failed:", err);
      }
      trace("BootDiag", "Guard retry rehydrate done", {
        autoRetryCount,
        elapsedMs: Math.round(performance.now() - guardMountRef.current),
        hasPosConfigNow: !!usePOSConfigStore.getState().posConfig,
        hasTillDataNow: !!useAuthStore.getState().tillData?.tillAccess,
      });
      setAutoRetryCount((c) => c + 1);
    }, delay);

    return () => clearTimeout(retryTimerRef.current);
  }, [posConfig, tillData, autoRetryCount]);

  if (posConfig && tillData) {
    return <PointOfsaleCore {...props} />;
  }

  const missing = !posConfig ? "POS Configuration" : "Till Data";

  // Failed after 3 retries — show error with manual actions
  if (phase === "failed") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#f9fafb",
          fontFamily: "Inter, sans-serif",
          padding: 32,
        }}
      >
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 24, color: "#dc2626" }}>!</span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, color: "#111827", marginBottom: 8 }}>Unable to Load POS</div>
        <div style={{ fontSize: 13, color: "#6b7280", textAlign: "center", maxWidth: 360, marginBottom: 8 }}>
          {missing} could not be loaded after {MAX_AUTO_RETRIES} attempts. This usually means the initial sync didn't complete or data was cleared.
        </div>
        <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 24 }}>Try logging out and syncing again. If the issue persists, contact support.</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={async () => {
              setAutoRetryCount(0);
              setPhase("retrying");
              try {
                await usePOSConfigStore.persist.rehydrate();
                await useAuthStore.getState().rehydrateFromStorage?.();
              } catch (err) {
                console.error("[POS] Manual rehydration retry failed:", err);
              }
            }}
            style={{ padding: "8px 20px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", cursor: "pointer", color: "#374151" }}
          >
            Retry
          </button>
          <button
            onClick={() => {
              window.location.href = "/";
            }}
            style={{ padding: "8px 20px", fontSize: 13, border: "none", borderRadius: 6, background: "#dc2626", cursor: "pointer", color: "#fff", fontWeight: 500 }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Loading / auto-retrying
  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f9fafb", fontFamily: "Inter, sans-serif" }}
    >
      <div
        style={{ width: 36, height: 36, border: "3px solid #e5e7eb", borderTopColor: "#A4CD38", borderRadius: "50%", animation: "spin 0.8s linear infinite", marginBottom: 16 }}
      />
      <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Loading POS Configuration...</div>
      <div style={{ fontSize: 12, color: "#9ca3af" }}>{autoRetryCount > 0 ? `Retry ${autoRetryCount}/${MAX_AUTO_RETRIES}...` : "Initializing"}</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PointOfsaleCoreGuard;
